// Updated: 2026-09-16 13:05:00 CDT
// Purpose: Edit screenplay emotion spans with deadline-aware adaptive playback.

import {
  Fragment,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { createPortal } from 'react-dom';
import {
  Download,
  Loader2,
  Play,
  Square,
  Trash2,
  Undo2,
  Redo2,
  Sparkles,
  PanelLeftClose,
  PanelLeftOpen,
  Volume2, WandSparkles, Speech, Languages, X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import ScriptZoomControl from './ScriptZoomControl';
import PerformanceSegmentLabels from './PerformanceSegmentLabels';
import PerformanceAttributeGutter from './PerformanceAttributeGutter';
import {clampScriptZoom,savedScriptZoom,savedToolsCollapsed,scriptZoomMetrics,SCRIPT_ZOOM_KEY,TOOLS_COLLAPSED_KEY} from '@/utils/scriptZoom';
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import { supabase } from "@/integrations/supabase/client";
import { loadStoredVoiceCatalog } from "@/utils/storedVoiceCatalog";
import { resolvePreviewVoice } from '@/utils/previewVoice';
import type {
  Character,
  TedIntonationSpan,
} from "@/types/screenplay";
import {
  isLocalAudio,
  createSentenceAudioBlocks,
  type SentenceAudioBlock,
} from "@/utils/audioBlockMap";
import { BulletproofSequencer } from "@/utils/bulletproofSequencer";
import { audioSourceBlockIds, cachedAudioPieces, groupCachedAudioBlocks, createCachedSelectionAudioBlocks } from '@/utils/cachedAudioBlocks';
import { previewSessionCacheKey } from "../../supabase/functions/_shared/preview-cache";
import { prepareTextForSpeech } from "@/utils/speechText";
import { prepareAuthoredAudio } from "@/utils/authoredAudio";
import { useServerAudioPreparation } from "@/hooks/useServerAudioPreparation";
import {usePlayToConvert} from '@/hooks/usePlayToConvert';
import {processAudioEffects} from '@/utils/audioEffects';
import { sceneAudioProgress, playbackBlockProgress } from '@/utils/conversionProgress';
import {useTtsRouterSettings} from '@/hooks/useTtsRouterSettings';
import { useAdministrator } from "@/hooks/useAdministrator";
import { canonicalJson } from "@/utils/cachedPlayback";
import { ScheduledAudio } from "@/utils/scheduledAudio";
import { AdaptivePlayback, PlaybackEstimates, PLAYBACK_PROGRESS_DELAY_MS, type PlaybackProgress } from "@/utils/adaptivePlayback";
import { playbackManifest, type PlaybackManifestClip } from "@/utils/playbackManifest";
import { playbackVoiceAssignments } from "@/utils/playbackVoiceAssignments";
import { withCastingBaselines, withCharacterAnnouncements, type CastingState } from '@/utils/castingAudio';
import { blockWithinConversionPages, withinConversionPages } from '@/utils/screenplayPages';
import { exportScreenplayMp3, MP3_SAMPLE_RATE, mp3Filename, type Mp3Progress } from "@/utils/mp3Export";
import {
  editorBlocksToSequence,
  highlightAfterBlockSelection,
  rebuildCharactersFromSequence,
  recentCharacterNames,
  sceneBlockRange,
  screenplaySequence,
  sequenceToEditorBlocks,
  type ScreenplayEditorBlock,
  type ScreenplayEditorBlockType,
  type ScreenplayTextHighlight,
} from "@/utils/screenplayEditor";
import {
  createTedIntonationSpec,
  tedEmotionTextRuns,
} from "@/utils/tedIntonation";
import GeminiDeliveryPanel from "./GeminiDeliveryPanel";
import PointVocalCueTools from './PointVocalCueTools';
import EditorToolSection, { EditorToolSectionGroup, EDITOR_TOOL_SECTIONS_KEY } from './EditorToolSection';
import { insertInlineVocalCue } from '@/utils/inlineVocalCues';
import ScriptBlockInsertBar from './ScriptBlockInsertBar';
import AssignInflectionsDialog from './AssignInflectionsDialog';
import { applyDirectedEntries } from '@/utils/sceneDirecting';
import { patchDeliveryRange, commonDeliveryDirection, commonDirectionValues, clearDeliveryRanges, overlappingDeliveryRanges, resolveDeliverySelection, DELIVERY_SELECTION_CONFLICT } from "@/utils/geminiDelivery";
import type { EditorGeminiDirection as GeminiDirection } from "@/utils/pauseDirections";
import {
  DEFAULT_SCREENPLAY_TEXT_SIZE,
  screenplayTextSizeClass,
  type ScreenplayTextSize,
} from "@/utils/screenplayTextSize";
import { DEFAULT_SCREENPLAY_BACKGROUND, type ScreenplayBackground } from "@/utils/screenplayAppearance";
import ScreenplaySaveStatus from "@/components/ScreenplaySaveStatus";
import SentenceAudioRecoveryDialog from './SentenceAudioRecoveryDialog';
import type { ScreenplaySaveState } from "@/utils/screenplaySaveQueue";
import { EditorHistory, historyShortcut } from "@/utils/editorHistory";
import { SPEECH_LANGUAGES, withSequenceLanguage, restoreSpeechLanguage, type SpeechLanguage } from '@/utils/speechLanguage';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { editEditorText, insertInlinePause, isPauseMarker, replaceEditorText, setInlinePauseDuration } from "@/utils/inlinePauses";
import { EDITOR_DRAG_TYPE, isPointInsert, readEditorDrag, textareaDropCaret, type EditorInsertKind } from "@/utils/editorDrag";
import {audioAvailability} from '@/utils/audioAvailability';

type EditorFocus = { blockId: string; field: string; start: number; end: number; direction: "forward" | "backward" | "none" };
type EditorSnapshot = {
  blocks: ScreenplayEditorBlock[]; activeBlockId: string; selectedBlockIds: string[];
  rangeAnchorIndex: number;
  highlightedText: ScreenplayTextHighlight | null; wholeBlockSelection: boolean; focus?: EditorFocus;
};
type EditorEdit = { group?: string; beforeFocus?: EditorFocus };

interface PreviewVoice {
  id: string;
  display_name: string;
  updated_at: string;
  provider: string;
}

interface PreparedPreview { url: string; persisted: boolean; voiceRevision: string; cache: string; requestMs: number; partial?:boolean }

export interface ConversionAnalysisStatus {
  completed:number;total:number;status:string;working:boolean;error:string;
  onPause?:()=>void;onResume:()=>void;onCancel?:()=>void;
}
interface ScreenplayEditorProps {
  conversionAnalysis?:ConversionAnalysisStatus;
  castingState?: CastingState;
  sayCharacterName?: boolean;
  screenplayId: string;
  screenplayFilename?: string | null;
  characters: Character[];
  voiceAssignments: Record<string, string>;
  onCharactersChange: (characters: Character[]) => void;
  onSave: () => Promise<boolean>;
  saveState: ScreenplaySaveState;
  previewDisabledReason?: string;
  preferredPreviewVoiceId?: string;
  textSize?: ScreenplayTextSize;
  background?: ScreenplayBackground;
  spellCheck?: boolean;
  scriptLanguage?: SpeechLanguage;
}

type PlaybackScope = "scene" | "selected" | "highlight" | "current" | "script";

const FONT_FAMILY = '"Courier New", Courier, monospace';

const blockLabel = (type: ScreenplayEditorBlockType) => {
  if (type === "scene-heading") return "Scene";
  if (type === "dialogue") return "Character / Dialogue";
  if (type === "transition") return "Transition";
  return "Narration";
};

const emptyBlock = (
  type: ScreenplayEditorBlockType,
  character?: string,
): ScreenplayEditorBlock => ({
  id: crypto.randomUUID(),
  type,
  text: type === "transition" ? "CUT TO:" : "",
  character: type === "dialogue" ? character ?? "" : undefined,
});

const rowsForText = (text: string, minimum = 1) =>
  Math.max(minimum, text.split("\n").length, Math.ceil(text.length / 62));

const fitTextArea = (element: HTMLTextAreaElement) => {
  element.style.height = "0px";
  const style=getComputedStyle(element);
  const borders=(parseFloat(style.borderTopWidth)||0)+(parseFloat(style.borderBottomWidth)||0);
  element.style.height = `${Math.ceil(element.scrollHeight+borders)}px`;
};

const PREVIEW_AUDIO_CACHE_LIMIT = 40;
const PLAYBACK_PREPARE_CONCURRENCY = 2;

function createTaskLimiter(concurrency: number) {
  const queue: Array<() => Promise<void>> = [];
  let activeTasks = 0;

  const runNext = () => {
    while (activeTasks < concurrency && queue.length > 0) {
      const queuedTask = queue.shift();
      if (!queuedTask) break;
      activeTasks += 1;
      void queuedTask().finally(() => {
        activeTasks -= 1;
        runNext();
      });
    }
  };

  return function limitTask<T>(task: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      queue.push(async () => {
        try {
          resolve(await task());
        } catch (error) {
          reject(error);
        }
      });
      runNext();
    });
  };
}

const performanceTextLayer = (text: string, spans: TedIntonationSpan[]) =>
  tedEmotionTextRuns(text, spans).map((run, index) => (
    <span
      key={`${index}-${run.text}`}
      data-delivery-range={run.spanId}
      data-performance-direction={run.direction}
    >
      {run.text}
    </span>
  ));

export default function ScreenplayEditor({
  conversionAnalysis,
  castingState, sayCharacterName = false,
  screenplayId,
  screenplayFilename,
  characters,
  voiceAssignments,
  onCharactersChange,
  onSave,
  saveState,
  previewDisabledReason,
  preferredPreviewVoiceId = "",
  textSize = DEFAULT_SCREENPLAY_TEXT_SIZE,
  background = DEFAULT_SCREENPLAY_BACKGROUND,
  spellCheck = true,
  scriptLanguage = 'auto',
}: ScreenplayEditorProps) {
  const isAdministrator = useAdministrator();
  const [assignInflectionsOpen, setAssignInflectionsOpen] = useState(false);
  const [pendingBlockLanguage, setPendingBlockLanguage] = useState<{blockId:string;value:string}|null>(null);
  const characterListId = useId();
  const [blocks, setBlocks] = useState<ScreenplayEditorBlock[]>(() => {
    const restored = sequenceToEditorBlocks(screenplaySequence(characters));
    return restored.length > 0 ? restored : [emptyBlock("scene-heading")];
  });
  const [activeBlockId, setActiveBlockId] = useState(blocks[0]?.id ?? "");
  const routingSettings=useTtsRouterSettings();
  const [selectedBlockIds, setSelectedBlockIds] = useState<Set<string>>(
    new Set(blocks[0] ? [blocks[0].id] : []),
  );
  // Explicit checkbox selection is independent of the active text/caret selection.
  const [checkedBlockIds,setCheckedBlockIds]=useState<Set<string>>(()=>new Set());
  const [rangeAnchorIndex, setRangeAnchorIndex] = useState(0);
  const historyRef = useRef(new EditorHistory<EditorSnapshot>());
  const blocksRef = useRef(blocks);
  const [historyVersion, setHistoryVersion] = useState(0);
  const [historyStatus, setHistoryStatus] = useState("");
  const editorRootRef = useRef<HTMLDivElement | null>(null);
  const [scriptZoom,setScriptZoom]=useState(savedScriptZoom);
  const [toolsCollapsed,setToolsCollapsed]=useState(savedToolsCollapsed);
  const [inspectedSpanId,setInspectedSpanId]=useState<string|null>(null);
  const [inspectedBlockId,setInspectedBlockId]=useState<string|null>(null);
  const [rightToolsNode,setRightToolsNode]=useState<HTMLDivElement|null>(null);
  const [activeRightTool,setActiveRightTool]=useState<string|null>(null);
  const rightTools=[['Voice controls',Volume2],['AI direction',WandSparkles],['Vocal cues & pauses',Speech],['Language',Languages]] as const;
  const inspectedBlock=useMemo(()=>blocks.find(block=>block.id===inspectedBlockId),[blocks,inspectedBlockId]);
  const inspectedSpan=inspectedBlock?.intonation?.spans.find(span=>span.id===inspectedSpanId);
  useEffect(()=>{try{localStorage.setItem(SCRIPT_ZOOM_KEY,String(scriptZoom));}catch{/* View controls work without storage. */}},[scriptZoom]);
  useEffect(()=>{try{localStorage.setItem(TOOLS_COLLAPSED_KEY,String(toolsCollapsed));}catch{/* View controls work without storage. */}},[toolsCollapsed]);
  const beforeInputFocusRef = useRef<EditorFocus | undefined>(undefined);
  const restoreFocusRef = useRef<EditorFocus | undefined>(undefined);
  const [cursor, setCursor] = useState<EditorFocus | null>(null);
  const dragKindRef = useRef<EditorInsertKind | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [pauseDrop, setPauseDrop] = useState<({ blockId: string } & NonNullable<ReturnType<typeof textareaDropCaret>>) | null>(null);
  const [highlightedText, setHighlightedText] = useState<ScreenplayTextHighlight | null>(null);
  const [wholeBlockSelection, setWholeBlockSelection] = useState(false);
  const [selectionIssue, setSelectionIssue] = useState("");
  const rejectedSelectionRef = useRef<string | null>(null);
  const [previewVoices, setPreviewVoices] = useState<PreviewVoice[]>([]);
  const voiceRevisions = useMemo(() => new Map(previewVoices.map(voice => [voice.id, voice.updated_at])), [previewVoices]);
  const [previewVoiceId, setPreviewVoiceId] = useState(preferredPreviewVoiceId);
  const [playbackScope, setPlaybackScope] = useState<PlaybackScope>("scene");
  const [isPlaying, setIsPlaying] = useState(false);
  const playToConvert=usePlayToConvert(screenplayId);
  const [playingBlockId, setPlayingBlockId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<Mp3Progress | null>(null);
  const [exportStatus, setExportStatus] = useState("");
  const [exportDownload, setExportDownload] = useState<{ url: string; filename: string } | null>(null);
  const exportControllerRef = useRef<AbortController | null>(null);
  const exportUrlRef = useRef<string | null>(null);
  const [playbackStatus, setPlaybackStatus] = useState("");
  const [playbackProgress, setPlaybackProgress] = useState<PlaybackProgress | null>(null);
  const [playbackConversionIds,setPlaybackConversionIds]=useState<string[]>([]);
  const isApplyingIntonation = false;
  const [intonationPreparation, setIntonationPreparation] = useState("");
  const playbackTokenRef = useRef(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const scheduledAudioRef = useRef<ScheduledAudio | null>(null);
  const previewAudioCacheRef = useRef(new Map<string, PreparedPreview>());
  const previewInFlightRef = useRef(new Map<string, Promise<PreparedPreview>>());
  const playbackEstimatesRef = useRef(new PlaybackEstimates());
  const adaptivePlaybackRef = useRef<AdaptivePlayback | null>(null);
  const screenplayDocumentRef = useRef<HTMLDivElement | null>(null);
  const sourceSequenceRef = useRef(JSON.stringify(screenplaySequence(characters)));
  const visibleRef = useRef(false);
  const visibilityEpochRef = useRef(0);
  const pagePositionRef = useRef<{ x: number; y: number } | null>(null);
  const textAreaLayoutRef = useRef(new WeakMap<HTMLTextAreaElement, {
    value: string; width: number; textSize: ScreenplayTextSize; zoom:number; lineHeight:string;
  }>());

  // Ignore our own parent echo (including empty draft blocks). Reconcile edits
  // made on the Voices page, such as a character rename, when returning here.
  useLayoutEffect(() => {
    const sequence = screenplaySequence(characters);
    const serialized = JSON.stringify(sequence);
    if (serialized === sourceSequenceRef.current) return;
    sourceSequenceRef.current = serialized;
    const incoming = new Map(sequenceToEditorBlocks(sequence).map((block) => [block.id, block]));
    const reconciled = blocksRef.current.map((block) => {
      const updated = incoming.get(block.id);
      return updated ? { ...block, character: updated.character, parenthetical: updated.parenthetical } : block;
    });
    // Never let an older editor snapshot overwrite changes made on another page.
    historyRef.current.clear(); setHistoryVersion(v => v + 1); setHistoryStatus("");
    blocksRef.current = reconciled; setBlocks(reconciled);
  }, [characters]);

  const activeIndex = Math.max(
    0,
    blocks.findIndex((block) => block.id === activeBlockId),
  );
  const recentCharacters = useMemo(
    () => recentCharacterNames(blocks),
    [blocks],
  );
  const activeBlock = blocks[activeIndex] ?? null;
  const activeTedSpans = activeBlock?.intonation?.spans ?? [];
  const [performanceActionsNode, setPerformanceActionsNode] = useState<HTMLDivElement | null>(null);
  const [performanceControlsNode, setPerformanceControlsNode] = useState<HTMLDivElement | null>(null);
  const [playbackToolbarNode, setPlaybackToolbarNode] = useState<HTMLDivElement | null>(null);
  const [editorToolbarNode,setEditorToolbarNode]=useState<HTMLDivElement|null>(null);
  const [exportToolbarNode,setExportToolbarNode]=useState<HTMLDivElement|null>(null);
  const [summaryNode, setSummaryNode] = useState<HTMLDivElement | null>(null);
  const [performanceMessagesNode,setPerformanceMessagesNode]=useState<HTMLDivElement|null>(null);
  useEffect(() => {
    const root=editorRootRef.current;
    const toolbar=editorToolbarNode?.firstElementChild;
    const fit=()=>{
      root?.style.setProperty('--editor-top',`${Math.max(0,root.getBoundingClientRect().top)}px`);
      if(toolbar)root?.style.setProperty('--performance-toolbar-height',`${toolbar.getBoundingClientRect().height}px`);
    };
    fit();window.addEventListener('resize',fit);
    const observer=typeof ResizeObserver==='undefined'?null:new ResizeObserver(fit);
    if(root?.parentElement)observer?.observe(root.parentElement);
    if(toolbar)observer?.observe(toolbar);
    return()=>{window.removeEventListener('resize',fit);observer?.disconnect();};
  },[editorToolbarNode]);
  const paneGridRef = useRef<HTMLDivElement | null>(null);
  const emotionPaneRef = useRef<HTMLElement | null>(null);
  const deliveryPaneRef = useRef<HTMLElement | null>(null);
  const selectedDeliveryRanges = highlightedText?.blockId === activeBlock?.id
    ? overlappingDeliveryRanges(activeTedSpans, highlightedText.start, highlightedText.end) : [];
  const selectedDelivery = selectedDeliveryRanges.length === 1 ? selectedDeliveryRanges[0] : undefined;
  const markerOnly = selectedDelivery?.start===highlightedText?.start && selectedDelivery?.end===highlightedText?.end;
  const selectedPause = markerOnly && isPauseMarker(selectedDelivery) ? selectedDelivery : undefined;
  useEffect(()=>{if(selectedPause)setActiveRightTool('Vocal cues & pauses');},[selectedPause&&selectedPause.id]);
  // A block-margin click is an explicit selection, not a collapsed text caret.
  // Keep partial-text selections separate so a stale block selection cannot expand them.
  const deliverySelections: ScreenplayTextHighlight[] = highlightedText ? [highlightedText] : wholeBlockSelection
    ? blocks.filter(block => selectedBlockIds.has(block.id) && block.text.trim()).map(block => ({
      blockId: block.id, start: 0, end: block.text.length, text: block.text,
    })) : [];
  const deliveryDirection = commonDirectionValues(deliverySelections.map(range => commonDeliveryDirection(
    blocks.find(block => block.id === range.blockId)?.intonation?.spans ?? [], range.start, range.end)));
  const nonGeminiSelection = deliverySelections.some(range => {
    const block = blocks.find(candidate => candidate.id === range.blockId);
    const voice = voiceAssignments[block?.type === 'dialogue' ? block.character ?? '' : 'SCENE NARRATOR'] || previewVoiceId;
    return previewVoices.find(candidate => candidate.id === voice)?.provider !== 'gemini';
  });
  const screenplayFontSizeClass = `${screenplayTextSizeClass(textSize)} screenplay-zoom-text`;
  const zoomMetrics=scriptZoomMetrics(textSize,scriptZoom);
  const sceneNumbersById = useMemo(() => new Map(
    blocks.filter((block) => block.type === "scene-heading")
      .map((block, index) => [block.id, index + 1]),
  ), [blocks]);

  useEffect(() => {
    let cancelled = false;

    const loadVoices = async () => {
      try {
        const voices = await loadStoredVoiceCatalog<{ id: string; display_name: string; updated_at: string; provider: string; is_narrator: boolean }>(supabase, "id,display_name,updated_at,provider,is_narrator");
        if (cancelled) return;
        setPreviewVoices(voices);
        setPreviewVoiceId((current) => {
          if (voices.some((voice) => voice.id === preferredPreviewVoiceId)) {
            return preferredPreviewVoiceId;
          }
          if (voices.some((voice) => voice.id === current)) return current;
          return resolvePreviewVoice(voices);
        });
        console.debug("[ScreenplayEditor] Preview voices loaded:", {
          voiceCount: voices.length,
          preferredVoiceId: preferredPreviewVoiceId || null,
        });
      } catch {
        if (!cancelled) toast.error("Unable to load the full voice catalog. Please refresh.");
      }
    };

    void loadVoices();
    const refreshVoices = () => { if (!document.hidden) void loadVoices(); };
    window.addEventListener("focus", refreshVoices);
    document.addEventListener("visibilitychange", refreshVoices);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", refreshVoices);
      document.removeEventListener("visibilitychange", refreshVoices);
    };
  }, [preferredPreviewVoiceId]);

  useLayoutEffect(() => {
    const document = screenplayDocumentRef.current;
    const scrollContainer = document?.parentElement;
    const fit=()=>{
    const scrollTop = scrollContainer?.scrollTop ?? 0;
    const width = document?.clientWidth ?? 0;
    document?.querySelectorAll<HTMLTextAreaElement>('textarea[data-editor-field="text"]')
      .forEach((element) => {
        const previous = textAreaLayoutRef.current.get(element);
        const lineHeight=getComputedStyle(element).lineHeight;
        if (previous?.value === element.value && previous.width === width && previous.textSize === textSize&&previous.zoom===scriptZoom&&previous.lineHeight===lineHeight) return;
        fitTextArea(element);
        textAreaLayoutRef.current.set(element, { value: element.value, width, textSize,zoom:scriptZoom,lineHeight });
      });
    // Temporarily shrinking textareas can move the browser's scroll anchor.
    if (scrollContainer) scrollContainer.scrollTop = scrollTop;
    };
    fit();
    // Pane preferences restore after mount; resizing must also refit wrapped text.
    // The cached width prevents observer feedback when only textarea heights change.
    const observer=typeof ResizeObserver==='undefined'?null:new ResizeObserver(fit);
    if(scrollContainer)observer?.observe(scrollContainer);
    if(document)observer?.observe(document);
    window.addEventListener('resize',fit);
    return()=>{observer?.disconnect();window.removeEventListener('resize',fit);};
  }, [blocks, textSize,scriptZoom,toolsCollapsed]);

  useLayoutEffect(() => {
    // Activity disconnects effects when hidden without destroying local state.
    // Stop audio synchronously on hide and retain a stopped playback UI.
    visibleRef.current = true;
    const cache = previewAudioCacheRef.current;
    const inFlight = previewInFlightRef.current;
    if (pagePositionRef.current) window.scrollTo(pagePositionRef.current.x, pagePositionRef.current.y);
    return () => {
      pagePositionRef.current = { x: window.scrollX, y: window.scrollY };
      visibleRef.current = false;
      visibilityEpochRef.current += 1;
      playbackTokenRef.current += 1;
      exportControllerRef.current?.abort();
      exportControllerRef.current = null;
      if (exportUrlRef.current) URL.revokeObjectURL(exportUrlRef.current);
      exportUrlRef.current = null;
      setIsExporting(false); setExportProgress(null); setExportStatus(""); setExportDownload(null);
      audioRef.current?.pause();
      audioRef.current = null;
      adaptivePlaybackRef.current?.stop();
      scheduledAudioRef.current?.stop();
      scheduledAudioRef.current = null;
      cache.forEach(({ url }) => URL.revokeObjectURL(url));
      cache.clear();
      inFlight.clear();
      setIsPlaying(false);
      setPlaybackStatus("");
      setPlaybackProgress(null);
    };
  }, []);

  const editorFocus = (element: Element | null = document.activeElement): EditorFocus | undefined => {
    if (!(element instanceof HTMLTextAreaElement || element instanceof HTMLInputElement) || !element.dataset.editorBlock) return;
    return { blockId: element.dataset.editorBlock, field: element.dataset.editorField ?? "text",
      start: element.selectionStart ?? 0, end: element.selectionEnd ?? 0, direction: element.selectionDirection ?? "none" };
  };
  const snapshot = (focus = editorFocus() ?? cursor ?? undefined): EditorSnapshot => ({
    blocks: blocksRef.current, activeBlockId, selectedBlockIds: [...selectedBlockIds], rangeAnchorIndex,
    highlightedText, wholeBlockSelection, focus: wholeBlockSelection ? undefined : focus,
  });
  const publishBlocks = (nextBlocks: ScreenplayEditorBlock[], edit: EditorEdit = {}, restoring = false) => {
    if(conversionAnalysis)return;
    const oldDirections=new Map(blocks.map(block=>[block.id,JSON.stringify(block.intonation)]));
    if(nextBlocks.some(block=>JSON.stringify(block.intonation)!==oldDirections.get(block.id)))playToConvert.defer();
    if (nextBlocks.length === blocksRef.current.length && nextBlocks.every((block, i) =>
      block === blocksRef.current[i] || JSON.stringify(block) === JSON.stringify(blocksRef.current[i]))) return;
    if (!restoring) {
      historyRef.current.record(snapshot(edit.beforeFocus), edit.group);
      setHistoryVersion(v => v + 1); setHistoryStatus("");
    }
    blocksRef.current = nextBlocks;
    setIntonationPreparation("");
    setBlocks(nextBlocks);
    const sequence = editorBlocksToSequence(nextBlocks);
    sourceSequenceRef.current = JSON.stringify(sequence);
    onCharactersChange(rebuildCharactersFromSequence(sequence, characters));

    console.debug("[ScreenplayEditor] Editor state updated:", {
      blockCount: nextBlocks.length,
      persistedSequenceCount: sequence.length,
    });
  };

  const restoreHistory = (action: "undo" | "redo") => {
    if(conversionAnalysis)return;
    const restored = historyRef.current[action](snapshot());
    if (!restored) return;
    setHistoryVersion(v => v + 1);
    setSelectionIssue(""); rejectedSelectionRef.current = null; beforeInputFocusRef.current = undefined;
    setActiveBlockId(restored.activeBlockId);
    setSelectedBlockIds(new Set(restored.selectedBlockIds)); setRangeAnchorIndex(restored.rangeAnchorIndex);
    setCursor(restored.focus ?? null);
    setHighlightedText(restored.highlightedText);
    setWholeBlockSelection(restored.wholeBlockSelection);
    restoreFocusRef.current = restored.wholeBlockSelection ? undefined : restored.focus ?? { blockId: restored.activeBlockId, field: "text", start: 0, end: 0, direction: "none" };
    publishBlocks(restored.blocks, {}, true);
    setHistoryStatus(action === "undo" ? "Last change undone." : "Change redone.");
  };
  const historyActionRef = useRef(restoreHistory);
  useLayoutEffect(() => { historyActionRef.current = restoreHistory; });
  useLayoutEffect(() => {
    const focus = restoreFocusRef.current;
    restoreFocusRef.current = undefined;
    if (!focus) return;
    const field = [...(editorRootRef.current?.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("[data-editor-field]") ?? [])]
      .find(el => el.dataset.editorBlock === focus.blockId && el.dataset.editorField === focus.field);
    field?.focus({ preventScroll: true });
    field?.setSelectionRange(focus.start, focus.end, focus.direction);
  }, [historyVersion]);
  useEffect(() => {
    const root = editorRootRef.current;
    const beforeInput = (event: Event) => {
      const input = event as InputEvent;
      const focus = editorFocus(event.target as Element);
      if (!focus) return; // Custom-direction drafts keep native field undo.
      if (input.inputType === "historyUndo" || input.inputType === "historyRedo") {
        event.preventDefault(); event.stopPropagation();
        historyActionRef.current(input.inputType === "historyUndo" ? "undo" : "redo");
      } else {
        beforeInputFocusRef.current = focus;
        if (!["insertText", "deleteContentBackward", "deleteContentForward", "insertCompositionText"].includes(input.inputType) || focus.start !== focus.end) historyRef.current.breakGroup();
      }
    };
    root?.addEventListener("beforeinput", beforeInput, true);
    return () => { root?.removeEventListener("beforeinput", beforeInput, true); historyRef.current.breakGroup(); };
  }, []);
  const textEdit = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): EditorEdit => {
    const type = (event.nativeEvent as InputEvent).inputType;
    const focus = beforeInputFocusRef.current ?? editorFocus(event.currentTarget);
    beforeInputFocusRef.current = undefined;
    return { beforeFocus: focus, group: !type || ["insertText", "deleteContentBackward", "deleteContentForward", "insertCompositionText"].includes(type)
      ? `${event.currentTarget.dataset.editorBlock}:${event.currentTarget.dataset.editorField}:${type ?? "typing"}` : undefined };
  };

  const updateBlock = (
    blockId: string,
    updates: Partial<ScreenplayEditorBlock>,
    edit?: EditorEdit,
  ) => {
    publishBlocks(
      blocksRef.current.map((block) =>
        block.id === blockId ? { ...block, ...updates } : block,
      ), edit,
    );
  };

  const updateBlockText = (block: ScreenplayEditorBlock, text: string, edit?: EditorEdit) => {
    if (block.text === text) return;
    const result = editEditorText(block, text);
    updateBlock(block.id, result.block, edit);
    const focus: EditorFocus = { blockId: block.id, field: "text", start: result.caret, end: result.caret, direction: "none" };
    setCursor(focus);
    if (result.block.text !== text) restoreFocusRef.current = focus;
    setHighlightedText(null); setSelectionIssue("");
  };

  const focusBlock = (
    blockId: string,
    field: "character" | "parenthetical" | "text" = "text",
  ) => {
    window.setTimeout(() => {
      if (!visibleRef.current) return;
      const element = document.querySelector<HTMLElement>(
        `[data-editor-block="${blockId}"][data-editor-field="${field}"]`,
      );
      element?.focus();
    }, 0);
  };

  const insertBlock = (
    type: ScreenplayEditorBlockType,
    afterIndex = activeIndex,
  ) => {
    const character = recentCharacters[0] ?? "";
    const block = emptyBlock(type, character);
    const nextBlocks = [...blocks];
    nextBlocks.splice(afterIndex + 1, 0, block);
    publishBlocks(nextBlocks);
    setActiveBlockId(block.id);
    setCursor(null); setHighlightedText(null);
    setSelectedBlockIds(new Set([block.id]));
    setRangeAnchorIndex(afterIndex + 1);
    focusBlock(block.id, type === "dialogue" ? "character" : "text");
  };

  const insertParenthetical = (afterIndex = activeIndex, atBoundary = false) => {
    const block = blocks[afterIndex];

    if (!atBoundary && block?.type === "dialogue") {
      updateBlock(block.id, {
        parenthetical: block.parenthetical?.trim() ? block.parenthetical : "()",
        parentheticalId: block.parentheticalId ?? crypto.randomUUID(),
      });
      focusBlock(block.id, "parenthetical");
      return;
    }

    const dialogue = {
      ...emptyBlock("dialogue", recentCharacters[0] ?? ""),
      parenthetical: "()",
      parentheticalId: crypto.randomUUID(),
    };
    const nextBlocks = [...blocks];
    nextBlocks.splice(afterIndex + 1, 0, dialogue);
    publishBlocks(nextBlocks);
    setActiveBlockId(dialogue.id);
    setSelectedBlockIds(new Set([dialogue.id]));
    setRangeAnchorIndex(afterIndex + 1);
    setCursor(null); setHighlightedText(null);
    focusBlock(dialogue.id, "parenthetical");
  };

  const removeBlock = (blockId: string) => {
    if (blocks.length === 1) {
      updateBlock(blockId, { text: "", character: undefined, type: "action", intonation: undefined });
      setCursor(null); setHighlightedText(null);
      return;
    }

    const removedIndex = blocks.findIndex((block) => block.id === blockId);
    const nextBlocks = blocks.filter((block) => block.id !== blockId);
    const nextActive = nextBlocks[Math.max(0, removedIndex - 1)] ?? nextBlocks[0];
    publishBlocks(nextBlocks);
    setActiveBlockId(nextActive.id);
    setSelectedBlockIds(new Set([nextActive.id]));
    setCursor(null); setHighlightedText(null);
  };

  const selectBlock = (
    blockId: string,
    index: number,
    shiftKey: boolean,
    outsideText = false,
  ) => {
    if (blockId !== activeBlockId) { setSelectionIssue(""); rejectedSelectionRef.current = null; setCursor(current => current?.blockId === blockId ? current : null); }
    setActiveBlockId(blockId);
    setWholeBlockSelection(outsideText);
    setHighlightedText(current=>outsideText?null:highlightAfterBlockSelection(current,blockId));
    if(outsideText){setCursor(null);setSelectionIssue('');rejectedSelectionRef.current=null;}

    if (shiftKey) {
      const start = Math.min(rangeAnchorIndex, index);
      const end = Math.max(rangeAnchorIndex, index);
      setSelectedBlockIds(new Set(blocks.slice(start, end + 1).map((block) => block.id)));
      return;
    }

    setRangeAnchorIndex(index);
    setSelectedBlockIds(new Set([blockId]));
  };

  const checkBlock = (blockId:string,index:number,checked:boolean) => {
    const next=new Set(checkedBlockIds);if(checked)next.add(blockId);else next.delete(blockId);
    setCheckedBlockIds(next);setSelectedBlockIds(next);
    setActiveBlockId(blockId);setRangeAnchorIndex(index);setWholeBlockSelection(true);
    setHighlightedText(null);setCursor(null);setSelectionIssue('');rejectedSelectionRef.current=null;
  };

  const handleTextSelection = (
    blockId: string,
    element: HTMLTextAreaElement,
  ) => {
    setWholeBlockSelection(false);
    setActiveBlockId(blockId);
    setCursor({ blockId, field: "text", start: element.selectionStart, end: element.selectionEnd, direction: element.selectionDirection });
    // Ignore the synthetic select/click caused by collapsing a rejected drag.
    if (rejectedSelectionRef.current === blockId) return;
    const block = blocks.find(candidate => candidate.id === blockId);
    const selection = resolveDeliverySelection(element.value, block?.intonation?.spans ?? [], element.selectionStart, element.selectionEnd);
    if (selection?.blocked) {
      rejectedSelectionRef.current = blockId;
      setSelectionIssue(DELIVERY_SELECTION_CONFLICT);
      setHighlightedText(null);
      const anchor = element.selectionDirection === "backward" ? element.selectionEnd : element.selectionStart;
      element.setSelectionRange(anchor, anchor);
      return;
    }
    setSelectionIssue("");
    setHighlightedText(selection ? { blockId, text: selection.text, start: selection.start, end: selection.end } : null);
  };

  const applyGeminiDelivery = async (direction: GeminiDirection, automatic = false) => {
    if (!deliverySelections.length || selectedPause) {
      toast.error("Highlight a word or phrase in the active screenplay block first.");
      return;
    }

    const ranges = new Map(deliverySelections.map(range => [range.blockId, range]));
    if (deliverySelections.some(range => blocks.find(block => block.id === range.blockId)?.text.slice(range.start, range.end) !== range.text)) {
      toast.error("The text changed. Select the passage again before applying directions.");
      return;
    }
    if (deliverySelections.some(range => {
      const block = blocks.find(candidate => candidate.id === range.blockId)!;
      return resolveDeliverySelection(block.text, block.intonation?.spans ?? [], range.start, range.end)?.blocked;
    })) {
      setSelectionIssue(DELIVERY_SELECTION_CONFLICT); return;
    }
    if (nonGeminiSelection) {
      toast.error("Assign a Gemini voice to this character or narration on the Voices page first.");
      return;
    }

    const patch=('audioGainDb' in direction||'volumeMotion' in direction)?{...direction,audioVolumeGroup:direction.audioGainDb!==undefined||direction.volumeMotion?crypto.randomUUID():undefined}:direction;
    const updatedBlocks = blocks.map(block => {
      const range = ranges.get(block.id);
      if (!range) return block;
      const current = block.intonation ?? createTedIntonationSpec();
      return { ...block, intonation: { ...current, sourceEmotion: 'neutral' as const,
        spans: patchDeliveryRange(current.spans, range.start, range.end, block.text, patch) } };
    });
    const inRange = updatedBlocks.filter(block => ranges.has(block.id) && blockWithinConversionPages(block.id, updatedBlocks, castingState?.page_limit));

    // Validate the new selection before changing the saved screenplay.
    try {
      // Outside the conversion range still needs authoring validation, but must
      // never enter the billable preparation list.
      const changedBlocks = updatedBlocks.filter(block => ranges.has(block.id));
      currentSentenceBlocks(changedBlocks.map(block => ({block})), updatedBlocks);
    }
    catch (error) { toast.error(error instanceof Error ? error.message : "Unable to apply directions."); return; }
    publishBlocks(updatedBlocks);
    if (!inRange.length) {
      if (!automatic) toast.info('Directions saved. This paragraph is outside the selected conversion pages; no audio was generated.');
      return;
    }
    if(!automatic)toast.success('Directions saved. Press Play to hear the changes.');
  };

  const insertPause = (blockId = cursor?.blockId, offset = cursor?.start, dropped = false) => {
    if (isPlaying || isExporting || isApplyingIntonation) return;
    const block = blocksRef.current.find(b => b.id === blockId);
    if (!block || offset === undefined || (!dropped && cursor?.start !== cursor?.end)) {
      toast.error("Place the cursor inside a screenplay text field, then click Pause, or drag Pause into the text."); return;
    }
    const result = insertInlinePause(block, offset);
    updateBlock(block.id, result.block);
    setActiveBlockId(block.id); setSelectedBlockIds(new Set([block.id]));
    const focus: EditorFocus = { blockId: block.id, field: "text", start: result.marker.start, end: result.marker.start, direction: "none" };
    setCursor(focus); restoreFocusRef.current = focus;
    setSelectionIssue(""); rejectedSelectionRef.current = null;
    setHighlightedText({ blockId: block.id, start: result.marker.start, end: result.marker.end, text: result.marker.selectedText });
  };
  const insertVocalCue = (kind: EditorInsertKind, blockId = cursor?.blockId, offset = cursor?.start, dropped = false) => {
    if (kind === 'pause') { insertPause(blockId, offset, dropped); return; }
    if (!kind.startsWith('cue:') || isPlaying || isExporting || isApplyingIntonation) return;
    const block = blocksRef.current.find(b => b.id === blockId);
    if (!block || offset === undefined || (!dropped && cursor?.start !== cursor?.end)) {
      toast.error('Place the cursor in screenplay text, or drag a vocal cue to a point in the text.'); return;
    }
    try {
      const result = insertInlineVocalCue(block, offset, kind.slice(4));
      currentSentenceBlocks([{block:result.block}], blocksRef.current.map(b=>b.id===block.id?result.block:b));
      updateBlock(block.id,result.block); setActiveBlockId(block.id); setSelectedBlockIds(new Set([block.id]));
      const focus: EditorFocus = {blockId:block.id,field:'text',start:result.start,end:result.start,direction:'none'};
      setCursor(focus);restoreFocusRef.current=focus;setSelectionIssue('');rejectedSelectionRef.current=null;
      setHighlightedText({blockId:block.id,start:result.start,end:result.end,text:result.block.text.slice(result.start,result.end)});
    } catch(error) { toast.error(error instanceof Error ? error.message : 'Unable to insert vocal cue.'); }
  };
  const endDrag = () => { dragKindRef.current = null; setDropIndex(null); setPauseDrop(null); };
  const startDrag = (event: React.DragEvent, kind: EditorInsertKind) => {
    event.dataTransfer.setData(EDITOR_DRAG_TYPE, kind); event.dataTransfer.effectAllowed = "copy";
    dragKindRef.current = kind;
  };
  const resolveDrop = (event: React.DragEvent) => {
    const kind = dragKindRef.current;
    if (!kind) return null;
    if (isPointInsert(kind)) {
      const input = (event.target as HTMLElement).closest<HTMLTextAreaElement>('textarea[data-editor-field="text"]');
      const caret = input && textareaDropCaret(input, event.clientX, event.clientY);
      return caret && input ? { kind, caret: { ...caret, blockId: input.dataset.editorBlock! } } : null;
    }
    const insertion=(event.target as HTMLElement).closest<HTMLElement>('[data-script-insert-position]');
    if(insertion)return {kind,index:Number(insertion.dataset.scriptInsertPosition)};
    const row = (event.target as HTMLElement).closest<HTMLElement>("[data-audio-block]");
    const index = row ? blocks.findIndex(b => b.id === row.dataset.audioBlock) : blocks.length - 1;
    const bounds = row?.getBoundingClientRect();
    return { kind, index: bounds && event.clientY < bounds.top + bounds.height / 2 ? index : index + 1 };
  };

  const clearDelivery = async (wholeBlock: boolean, automatic = false) => {
    try {
      if(!wholeBlock&&selectionIssue)throw new Error(DELIVERY_SELECTION_CONFLICT);
      const range=!wholeBlock?highlightedText:null;
      if(range&&(!activeBlock||range.blockId!==activeBlock.id||activeBlock.text.slice(range.start,range.end)!==range.text))throw new Error('Select the passage again before clearing its directions.');
      const targets=wholeBlock?new Set([activeBlockId]):range?new Set([range.blockId]):selectedBlockIds;
      if(!targets.size)throw new Error('Select screenplay text or blocks first.');
      const updatedBlocks=blocks.map(block=>{
        if(!targets.has(block.id)||!block.intonation?.spans.length)return block;
        const spans=clearDeliveryRanges(block.intonation.spans,block.text,range?.start,range?.end);
        return {...block,intonation:spans.length?{...block.intonation,spans}:undefined};
      });
      const changed=updatedBlocks.filter((block,index)=>block!==blocks[index]);
      if(!changed.length)return;
      const inRange=changed.filter(block=>blockWithinConversionPages(block.id,updatedBlocks,castingState?.page_limit));
      currentSentenceBlocks(inRange.map(block=>({block})),updatedBlocks);
      publishBlocks(updatedBlocks);setIntonationPreparation('');
      // Like direct control edits, one durable plan coalesces all affected blocks.
      if(!automatic)toast.success(inRange.length?'Directions cleared. Press Play to hear the changes.':'Directions cleared. These blocks are outside the selected conversion pages; no audio was generated.');
    } catch (error) {
      toast.error(error instanceof Error?error.message:'Unable to clear directions.');
    }
  };

  const handleEditorShortcut = (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
    block: ScreenplayEditorBlock,
    index: number,
  ) => {
    rejectedSelectionRef.current = null;
    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      event.currentTarget.selectionStart === event.currentTarget.value.length &&
      event.currentTarget.selectionEnd === event.currentTarget.value.length
    ) {
      event.preventDefault();
      insertBlock(block.type === "scene-heading" ? "action" : "action", index);
    }
  };

  const stopPlayback = () => {
    playbackTokenRef.current += 1;
    audioRef.current?.pause();
    audioRef.current = null;
    adaptivePlaybackRef.current?.stop();
    scheduledAudioRef.current?.stop();
    scheduledAudioRef.current = null;
    setIsPlaying(false);
    setPlayingBlockId(null);
    setPlaybackStatus("");
    setPlaybackProgress(null);
  };

  const loadPreview = async (
    voiceId: string,
    text: string,
    synthesisOptions: Record<string, unknown> = {},
    requirePersistent = false,
    knownRevision?: string,
    cacheSources?: SentenceAudioBlock['cacheSources'],
    allowStreamPrefix = false,
  ) => {
    const visibilityEpoch = visibilityEpochRef.current;
    if (!visibleRef.current) throw new Error("Editor preview was closed.");
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !sessionData.session?.access_token) {
      throw sessionError ?? new Error("Your session has expired. Sign in again.");
    }
    // Refresh this actor even if the editor stayed open while an admin changed it.
    // Playback already loaded current revisions in one bulk query. The server
    // independently checks the current actor on every durable-cache request.
    const { data: voice, error: voiceError } = knownRevision
      ? { data: { updated_at: knownRevision }, error: null }
      : await supabase.from("voices").select("updated_at").eq("id", voiceId)
        .eq("provider", "gemini").eq("is_active", true).single();
    if (voiceError || !voice?.updated_at) {
      throw voiceError ?? new Error("The selected voice is no longer available.");
    }
    setPreviewVoices(current => current.some(item => item.id === voiceId && item.updated_at !== voice.updated_at)
      ? current.map(item => item.id === voiceId ? { ...item, updated_at: voice.updated_at } : item) : current);
    const cacheInput = {
      userId: sessionData.session.user.id, voiceId,
      voiceRevision: voice.updated_at, text, synthesisOptions,
    };
    const cacheKey = previewSessionCacheKey(cacheInput);
    if (!visibleRef.current || visibilityEpoch !== visibilityEpochRef.current) {
      throw new Error("Editor preview was closed.");
    }
    const hasTedIntonation = Boolean(synthesisOptions.ted_intonation || synthesisOptions.gemini_intonation);
    const cachedUrl = previewAudioCacheRef.current.get(cacheKey);
    if (cachedUrl && (!requirePersistent || cachedUrl.persisted)) {
      previewAudioCacheRef.current.delete(cacheKey);
      previewAudioCacheRef.current.set(cacheKey, cachedUrl);
      console.debug("[ScreenplayEditor] Preview session-cache hit:", {
        voiceId,
        textCharacters: text.length,
        hasTedIntonation,
      });
      return { ...cachedUrl, cache: "memory", requestMs: 0 };
    }

    // Applying an emotion starts preparation immediately. Playback joins that
    // exact request (same user, actor revision, text and options) if still busy.
    const inFlightKey=cacheKey+(allowStreamPrefix?':opening':'');
    // An opening lookup may join a complete-audio lookup, never the reverse:
    // background preparation/export must not mistake a prefix for a full clip.
    const inFlight = previewInFlightRef.current.get(inFlightKey)
      ?? (allowStreamPrefix ? previewInFlightRef.current.get(cacheKey) : undefined);
    if (inFlight) return inFlight;
    const generation = (async () => {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
      const publishableKey = (
        import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
        import.meta.env.VITE_SUPABASE_ANON_KEY
      ) as string | undefined;

      if (!supabaseUrl || !publishableKey) {
        throw new Error("The screenplay preview endpoint is not configured.");
      }

      const requestId = crypto.randomUUID();
      const requestStarted = performance.now();
      const response = await fetch(`${supabaseUrl}/functions/v1/screenplay-preview`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${sessionData.session.access_token}`,
          "apikey": publishableKey,
          "Content-Type": "application/json",
          "X-Request-ID": requestId,
        },
        signal: AbortSignal.timeout(180000),
        body: JSON.stringify({
          screenplay_id: screenplayId,
          voice_id: voiceId,
          text,
          synthesis_options: synthesisOptions,
          ...(allowStreamPrefix?{stream_prefix:true}:{}),
          ...(cacheSources && cacheSources.length > 1 ? { cache_sources: cacheSources.map(s => ({ text:s.text,synthesis_options:s.synthesisOptions })) } : {}),
        }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({})) as { error?: string; code?: string };
        throw Object.assign(new Error(payload.error ?? `Preview returned HTTP ${response.status}`),
          { code: payload.code, retryable: !payload.code?.startsWith("GEMINI_") && ![400, 401, 403, 404, 409, 413, 422].includes(response.status) });
      }

      const blob = await response.blob();
      if (!visibleRef.current || visibilityEpoch !== visibilityEpochRef.current) {
        throw new Error("Editor preview was closed.");
      }
      if (!blob.type.includes("audio") || blob.size < 44) {
        throw new Error("The preview service returned invalid audio.");
      }

      console.debug("[ScreenplayEditor] Preview received:", {
        requestId,
        voiceId,
        textCharacters: text.length,
        hasTedIntonation,
        audioBytes: blob.size,
        contentType: blob.type,
        persistentCache: response.headers.get("X-TableRead-Preview-Cache"),
        requestMs: Math.round(performance.now() - requestStarted),
        stages: response.headers.get("Server-Timing"),
      });
      const generatedUrl = URL.createObjectURL(blob);
      const persistentCache = response.headers.get("X-TableRead-Preview-Cache");
      if(response.headers.get('X-TableRead-Stream-Prefix')==='true') {
        if(!allowStreamPrefix) {URL.revokeObjectURL(generatedUrl);throw new Error('Unexpected partial audio.');}
        return {url:generatedUrl,persisted:false,partial:true,cache:'partial',requestMs:performance.now()-requestStarted,
          voiceRevision:response.headers.get('X-TableRead-Preview-Voice-Revision')??voice.updated_at} as PreparedPreview;
      }
      const result: PreparedPreview = { url: generatedUrl,
        cache: persistentCache ?? "unknown", requestMs: performance.now() - requestStarted,
        persisted: persistentCache === "hit" || persistentCache === "miss",
        voiceRevision: response.headers.get("X-TableRead-Preview-Voice-Revision") ?? voice.updated_at };
      const returnedKey = previewSessionCacheKey({ ...cacheInput, voiceRevision: result.voiceRevision });
      const replaced = previewAudioCacheRef.current.get(returnedKey);
      if (replaced) URL.revokeObjectURL(replaced.url);
      previewAudioCacheRef.current.set(previewSessionCacheKey({
        ...cacheInput,
        voiceRevision: result.voiceRevision,
      }), result);

      while (previewAudioCacheRef.current.size > PREVIEW_AUDIO_CACHE_LIMIT) {
        const oldestEntry = previewAudioCacheRef.current.entries().next().value as
          | [string, PreparedPreview]
          | undefined;
        if (!oldestEntry) break;
        previewAudioCacheRef.current.delete(oldestEntry[0]);
        URL.revokeObjectURL(oldestEntry[1].url);
      }

      return result;
    })();
    previewInFlightRef.current.set(inFlightKey, generation);
    try { return await generation; }
    finally {
      if (previewInFlightRef.current.get(inFlightKey) === generation) {
        previewInFlightRef.current.delete(inFlightKey);
      }
    }
  };

  const allSentenceBlocks = useMemo(() => {
    if (!previewVoiceId || !blocks.some(block => block.text.trim())) return [];
    const sequence = editorBlocksToSequence(blocks);
    const currentCharacters = rebuildCharactersFromSequence(sequence, characters);
    return groupCachedAudioBlocks(withinConversionPages(cachedAudioPieces(createSentenceAudioBlocks(BulletproofSequencer.createBulletproofSequence(
      withSequenceLanguage(currentCharacters, scriptLanguage), playbackVoiceAssignments(currentCharacters, voiceAssignments, previewVoiceId),
    )), routingSettings), blocks, castingState?.page_limit),{...routingSettings,mergeAcrossBlocks:!sayCharacterName});
  }, [blocks, characters, voiceAssignments, previewVoiceId, castingState?.page_limit, sayCharacterName,routingSettings, scriptLanguage]);
  const playbackSentences = useMemo(() => withCharacterAnnouncements(allSentenceBlocks, sayCharacterName,
    voiceAssignments['SCENE NARRATOR'] ?? previewVoiceId), [allSentenceBlocks, sayCharacterName, voiceAssignments, previewVoiceId]);
  const preparationSentences = useMemo(() => withCastingBaselines(playbackSentences, castingState?.baseline_voices ?? {}),
    [playbackSentences, castingState]);
  const preparation = useServerAudioPreparation({
    screenplayId, sentences: preparationSentences, revisions: voiceRevisions, activeBlockId,
    castingState,
    expectedSpeechLanguage: scriptLanguage,
    documentRef: screenplayDocumentRef,
    paused: isPlaying || isApplyingIntonation || isExporting,
    enabled:routingSettings.ready&&!conversionAnalysis&&!playToConvert.pending,
  });
  const confirmedAudioStates=preparation.confirmedAudioStates??preparation.audioStates;
  const availableAudio=audioAvailability(preparation.progressKnown?preparation.completed:null,preparation.total);
  const sceneConversion=useMemo(()=>sceneAudioProgress(blocks,confirmedAudioStates,castingState?.page_limit),[blocks,confirmedAudioStates,castingState?.page_limit]);
  const blockConversion=useMemo(()=>playbackBlockProgress(playbackConversionIds,confirmedAudioStates),[playbackConversionIds,confirmedAudioStates]);
  const showBlockConversion=blockConversion.total>0&&blockConversion.completed<blockConversion.total&&!preparation.cancelled;
  const audioInputRevision = screenplayId + canonicalJson(preparationSentences.map(sentence => ({
    voice: sentence.voiceId, revision: voiceRevisions.get(sentence.voiceId),
    text: sentence.text, options: sentence.synthesisOptions, silenceMs: sentence.silenceMs,audioVolume:sentence.audioVolume,audioSpeed:sentence.audioSpeed,
  })));
  const lastAudioInputRevision = useRef(audioInputRevision);
  useLayoutEffect(() => {
    if (lastAudioInputRevision.current === audioInputRevision) return;
    lastAudioInputRevision.current = audioInputRevision;
    setPlaybackConversionIds([]);
    exportControllerRef.current?.abort(new Error("Screenplay or voice settings changed. Export again to include your changes."));
    if (exportUrlRef.current) URL.revokeObjectURL(exportUrlRef.current);
    exportUrlRef.current = null; setExportDownload(null);
    if (!exportControllerRef.current) { setExportProgress(null); setExportStatus(""); }
    // Never continue a captured playback plan after its words/actor/emotion change.
    adaptivePlaybackRef.current?.stop();
    scheduledAudioRef.current?.stop();
    playbackTokenRef.current++;
    audioRef.current?.pause();
    audioRef.current = null;
    setIsPlaying(false);
    setPlaybackStatus("");
    setPlaybackProgress(null);
  }, [audioInputRevision]);

  useEffect(() => {
    if (!isExporting) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [isExporting]);

  const currentSentenceBlocks = (
    previewBlocks: Array<{ block: ScreenplayEditorBlock; text?: string }>,
    sourceBlocks: ScreenplayEditorBlock[] = blocks,
  ): SentenceAudioBlock[] => {
    const selectedIds = new Set(
      previewBlocks
        .filter((item) => item.text === undefined)
        .map((item) => item.block.id),
    );
    if (selectedIds.size === 0) return [];
    if (sourceBlocks === blocks && !allSentenceBlocks.some(block => audioSourceBlockIds(block).some(id=>selectedIds.has(id)) && !audioSourceBlockIds(block).every(id=>selectedIds.has(id))))
      return allSentenceBlocks.filter(block => audioSourceBlockIds(block).every(id=>selectedIds.has(id)));

    const sequence = editorBlocksToSequence(sourceBlocks);
    const currentCharacters = rebuildCharactersFromSequence(sequence, characters);
    const resolvedVoiceAssignments = playbackVoiceAssignments(
      currentCharacters,
      voiceAssignments,
      previewVoiceId,
    );
    const sequenceMap = BulletproofSequencer.createBulletproofSequence(
      withSequenceLanguage(currentCharacters, scriptLanguage),
      resolvedVoiceAssignments,
    );

    return groupCachedAudioBlocks(withinConversionPages(cachedAudioPieces(createSentenceAudioBlocks(sequenceMap)), sourceBlocks, castingState?.page_limit)
      .filter((block) => selectedIds.has(block.sourceBlockId)),{mergeAcrossBlocks:!sayCharacterName,strategy:routingSettings.strategy});
  };

  const currentSelectionPreview = (
    item: {
      block: ScreenplayEditorBlock;
      text?: string;
      selectionStart?: number;
      selectionEnd?: number;
    },
    sourceBlocks: ScreenplayEditorBlock[] = blocks,
  ) => {
    if (item.selectionStart === undefined || item.selectionEnd === undefined) {
      return null;
    }

    const sequence = editorBlocksToSequence(sourceBlocks);
    const currentCharacters = rebuildCharactersFromSequence(sequence, characters);
    const resolvedVoiceAssignments = playbackVoiceAssignments(
      currentCharacters,
      voiceAssignments,
      previewVoiceId,
    );
    const sequenceMap = BulletproofSequencer.createBulletproofSequence(
      withSequenceLanguage(currentCharacters, scriptLanguage),
      resolvedVoiceAssignments,
    );

    return createCachedSelectionAudioBlocks(
      sequenceMap,
      item.block.id,
      Math.max(0, item.selectionStart - (item.block.text.length - item.block.text.trimStart().length)),
      Math.max(0, item.selectionEnd - (item.block.text.length - item.block.text.trimStart().length)),
    );
  };

  const cachedPlaybackUrls = async (
    sentenceBlocks: SentenceAudioBlock[],
    revisions: Map<string, string>,
    providers: Map<string, string>,
  ): Promise<Map<string, string>> => {
    if (!sentenceBlocks.some(sentence => !isLocalAudio(sentence))) return new Map();
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !sessionData.session?.user.id) {
      throw sessionError ?? new Error("Your session has expired. Sign in again.");
    }
    const voiceIds = [...new Set([...sentenceBlocks.filter(sentence => !isLocalAudio(sentence)).map(sentence => sentence.voiceId),
      ...(sayCharacterName ? [voiceAssignments["SCENE NARRATOR"] ?? previewVoiceId] : [])])];
    const { data: voices, error } = await supabase.from("voices")
      .select("id,updated_at,provider").in("id", voiceIds).eq("provider", "gemini").eq("is_active", true);
    if (error) throw error;
    (voices ?? []).forEach(voice => revisions.set(voice.id, voice.updated_at));
    (voices ?? []).forEach(voice => providers.set(voice.id, voice.provider));
    // Memory is only a fast path. Durable lookup and generation both go through
    // screenplay-preview, which validates the current actor and input settings.
    return new Map(sentenceBlocks.flatMap(sentence => {
      if (isLocalAudio(sentence)) return [];
      const voiceRevision = revisions.get(sentence.voiceId);
      if (!voiceRevision) return [];
      const url = previewAudioCacheRef.current.get(previewSessionCacheKey({
        userId: sessionData.session!.user.id,
        voiceId: sentence.voiceId, voiceRevision,
        text: sentence.text, synthesisOptions: sentence.synthesisOptions,
      }));
      return url ? [[sentence.id, url.url] as const] : [];
    }));
  };

  const playPreviewBlocks = async (
    previewBlocks: Array<{ block: ScreenplayEditorBlock; text?: string; selectionStart?: number; selectionEnd?: number }>,
    preferCachedAudio = false,
    sourceBlocks: ScreenplayEditorBlock[] = blocks,
  ): Promise<boolean> => {
    if (exportControllerRef.current) return false;
    if (previewDisabledReason || !previewVoiceId) {
      toast.error(previewDisabledReason || "Choose a preview voice first.");
      return false;
    }
    const allowedIds = new Set(allSentenceBlocks.flatMap(audioSourceBlockIds));
    const scopedPreview = castingState?.page_limit === undefined ? previewBlocks : previewBlocks.filter(item => allowedIds.has(item.block.id));
    if (!scopedPreview.length && previewBlocks.length) {
      toast.info('This selection is outside your conversion pages. Increase the page count on the Voices page to hear it.');
      return false;
    }
    previewBlocks = scopedPreview;
    playToConvert.authorize();
    stopPlayback();
    setPlaybackConversionIds([]);
    const playbackToken = playbackTokenRef.current;
    const current = () => visibleRef.current && playbackToken === playbackTokenRef.current;
    const began = performance.now();
    // Resume synchronously in the user's Play gesture, including delayed starts.
    const scheduled = new ScheduledAudio();
    scheduledAudioRef.current = scheduled;
    let queue: AdaptivePlayback | null = null;
    let started = false;
    let playbackFailure = "";
    let waitingSince: number | null = began;
    let latestProgress: PlaybackProgress = { percent: 0, ready: 0, total: 0, estimatedWaitSeconds: 0 };
    setIsPlaying(true);
    setPlayingBlockId(previewBlocks[0]?.block.id ?? null);
    const progressTimer = setInterval(() => {
      if (!current()) return;
      const waiting = !started || scheduled.remainingSeconds <= 0;
      if (!waiting) { waitingSince = null; setPlaybackProgress(null); return; }
      waitingSince ??= performance.now();
      if (performance.now() - waitingSince < PLAYBACK_PROGRESS_DELAY_MS) return;
      const progress = queue?.progress() ?? latestProgress;
      setPlaybackProgress(previous => previous?.percent === progress.percent && previous.ready === progress.ready &&
        previous.total === progress.total && previous.estimatedWaitSeconds === progress.estimatedWaitSeconds ? previous : progress);
      setPlaybackStatus(progress.batchWaiting?'Waiting for background batch audio · Google targets completion within 24 hours.':`Loading selected audio · ${progress.percent}% ready (${progress.ready}/${progress.total} clips loaded).` +
        (progress.estimatedWaitSeconds > 0 ? ` Estimated wait: ${progress.estimatedWaitSeconds} sec.` : ""));
    }, 100);
    try {
      await scheduled.ready;
      if (!current()) return false;
      let sentences = preferCachedAudio ? previewBlocks.some(item => item.text !== undefined || item.selectionStart !== undefined)
        ? previewBlocks.flatMap(item => currentSelectionPreview(item, sourceBlocks) ?? currentSentenceBlocks([item], sourceBlocks))
        : currentSentenceBlocks(previewBlocks,sourceBlocks)
        : previewBlocks.flatMap((item, index): SentenceAudioBlock[] => {
          const selected = currentSelectionPreview(item, sourceBlocks);
          if (selected) return selected;
          const character = item.block.type === "dialogue" ? item.block.character?.trim().toUpperCase() ?? "" : "SCENE NARRATOR";
          const text = prepareTextForSpeech((item.text ?? item.block.text).trim(),
            item.block.type === "dialogue" ? "dialogue" : item.block.type);
          if (!text) return [];
          return [{ id: `selection:${item.block.id}:${index}`, sourceBlockId: item.block.id,
            sentenceIndex: 0, type: item.block.type, character, sourceText: text, text,
            originalOrder: index, sequenceIndex: index,
            voiceId: voiceAssignments[character] ?? previewVoiceId,
            synthesisOptions: {} }];
        });
      if (sayCharacterName) {
        const seen = new Set<string>();
        sentences = sentences.flatMap(sentence => {
          if (isLocalAudio(sentence) || sentence.type !== "dialogue" || !sentence.character.trim() ||
            sentence.character === "SCENE NARRATOR" || seen.has(sentence.sourceBlockId)) return [sentence];
          seen.add(sentence.sourceBlockId);
          const text = prepareTextForSpeech(sentence.character, "dialogue");
          return [{ ...sentence, id: `announcement:${sentence.sourceBlockId}`, text, sourceText: text,
            voiceId: voiceAssignments["SCENE NARRATOR"] ?? previewVoiceId, synthesisOptions: {}, cacheSources: undefined }, sentence];
        });
      }
      sentences = sentences.filter(s => !isLocalAudio(s) || s.silenceMs! > 0);
      setPlaybackConversionIds([...new Set(sentences.filter(s=>!isLocalAudio(s)).flatMap(audioSourceBlockIds))]);
      latestProgress.total = sentences.length;
      if (!sentences.length) {
        playbackFailure = "The selected text is marked not spoken and has no timed pause.";
        return false;
      }
      const revisions = new Map<string, string>();
      const providers = new Map<string, string>();
      const memory = await cachedPlaybackUrls(sentences, revisions, providers);
      if (!current()) return false;
      let manifest: PlaybackManifestClip[] = sentences.map(s => ({
        cached: isLocalAudio(s) || memory.has(s.id), url: null, voice_revision: revisions.get(s.voiceId) ?? "", bytes: 0,
      }));
      if (memory.size < sentences.length) {
        try { manifest = await playbackManifest(screenplayId, sentences, revisions); }
        catch (error) {
          if (error && typeof error === "object" && "status" in error &&
            [400, 401, 403, 404, 409, 413].includes(Number(error.status))) throw error;
          // A manifest outage is not proof that audio is ready. The conservative
          // path uses validated previews and waits for actual results.
          console.warn("[Playback] Cache manifest unavailable; verifying through previews.");
        }
      }
      if (!current()) return false;
      if(sentences.every((s,i)=>isLocalAudio(s)||memory.has(s.id)||manifest[i].cached))setPlaybackConversionIds([]);
      queue = new AdaptivePlayback(sentences.map((s, i) => ({
        text: s.text, emotional: Boolean(s.synthesisOptions.ted_intonation || s.synthesisOptions.gemini_intonation),
        silenceMs: s.silenceMs,
        cached: memory.has(s.id) || manifest[i].cached,
        provider: providers.get(s.voiceId),
      })), async index => {
        if (!current()) throw new Error("Playback stopped.");
        const sentence = sentences[index];
        if (isLocalAudio(sentence)) return { audio: scheduled.silence(sentence.silenceMs!), persisted: true };
        const cachedUrl = memory.get(sentence.id) ?? manifest[index].url;
        if (cachedUrl) {
          let decoded:AudioBuffer|undefined;
          try { decoded=await scheduled.decode(cachedUrl); }
          catch { if (!current()) throw new Error("Playback stopped."); }
          if(decoded)return {audio:await processAudioEffects(decoded,sentence.audioVolume,sentence.audioSpeed,scheduled.signal),persisted:true};
        }
        // Expired/corrupt cached URLs fall back to the exact actor/emotion path.
        // Never substitute neutral audio or skip an unavailable sentence.
        return prepareAuthoredAudio(sentence, async options => {
          if (!current()) throw Object.assign(new Error("Playback stopped."), { retryable: false });
          // Every clip required by this Play action is interactive. Restricting
          // promotion to index zero strands later cache misses when background
          // preparation is paused for playback (especially if clip zero is cached).
          // The server still owns leases, fairness, budgets and provider retries.
          const result = await loadPreview(sentence.voiceId, sentence.text, options, false, revisions.get(sentence.voiceId), sentence.cacheSources,!sentence.audioVolume&&!sentence.audioSpeed);
          if (!current() || result.voiceRevision !== revisions.get(sentence.voiceId)) {
            throw Object.assign(new Error("Voice settings changed. Please play again."), { retryable: false });
          }
          const audio = await processAudioEffects(await scheduled.decode(result.url),sentence.audioVolume,sentence.audioSpeed,scheduled.signal);
          if(result.partial) {
            URL.revokeObjectURL(result.url);
            const completion=(async()=>{
              while(current()) {
                try {
                  const final=await loadPreview(sentence.voiceId,sentence.text,options,true,revisions.get(sentence.voiceId),sentence.cacheSources);
                  if(final.voiceRevision!==revisions.get(sentence.voiceId))throw new Error('Voice settings changed. Please play again.');
                  return processAudioEffects(await scheduled.decode(final.url),sentence.audioVolume,sentence.audioSpeed,scheduled.signal);
                } catch(error) {
                  if(!current())throw new Error('Playback stopped.');
                  if(!error||typeof error!=='object'||!('code' in error)||!['GEMINI_QUEUED','GEMINI_BATCH_QUEUED','GEMINI_STORAGE_PENDING','GEMINI_STORAGE_UNAVAILABLE'].includes(String(error.code)))throw error;
                  await new Promise(resolve=>setTimeout(resolve,1000));
                }
              }
              throw new Error('Playback stopped.');
            })();
            return {audio,persisted:false,completion};
          }
          return { audio, persisted: result.persisted };
        });
      }, playbackEstimatesRef.current);
      adaptivePlaybackRef.current = queue;
      const progress = (value: PlaybackProgress) => { latestProgress = value; };
      await queue.waitForStart(progress);
      if (!current()) return false;
      console.debug("[PlaybackStartup]", { clips: sentences.length,
        cached: manifest.filter(c => c.cached).length, waitMs: Math.round(performance.now() - began),
        ...queue.progress() });
      let previousEnded: Promise<void> = Promise.resolve();
      for (let index = 0; index < sentences.length; index++) {
        const audio = await queue.get(index, progress);
        if (!current()) return false;
        let queued = scheduled.enqueue(audio, () => {
          if (!current()) return;
          started = true; waitingSince = null;
          setPlayingBlockId(sentences[index].sourceBlockId);
          setPlaybackProgress(null);
          setPlaybackStatus(`Playing block ${index + 1} of ${sentences.length}`);
        });
        const completion=queue.completion(index);
        if(completion) {
          const final=await completion;
          if(!current())return false;
          if(final.duration<audio.duration)throw new Error('Streamed recording is incomplete.');
          if(final.duration>audio.duration)queued=scheduled.enqueue(final,undefined,audio.duration);
        }
        console.debug("[PlaybackTiming]", { index,
          scheduledStartMs: Math.round(performance.now() - began + queued.startDelayMs),
          gapMs: Math.round(queued.gapMs) });
        // Schedule the next clip against the same clock before this one ends.
        await previousEnded;
        if (index > 0) queue.release(index - 1);
        previousEnded = queued.ended;
      }
      await previousEnded;
      return current();
    } catch (error) {
      if (!current()) return false;
      console.error("[ScreenplayEditor] Preview playback failed:", error);
      playbackFailure = error instanceof Error ? error.message : "Unable to play this screenplay selection.";
      toast.error(playbackFailure);
      return false;
    } finally {
      clearInterval(progressTimer);
      queue?.stop(); scheduled.stop();
      if (current()) {
        adaptivePlaybackRef.current = null; scheduledAudioRef.current = null; audioRef.current = null;
        setIsPlaying(false); setPlayingBlockId(null); setPlaybackProgress(null); setPlaybackStatus(playbackFailure);
      }
    }
  };

  const saveScreenplayMp3 = async () => {
    // Ref guards double clicks before React has rendered the disabled button.
    if (exportControllerRef.current || previewDisabledReason || !playbackSentences.length) return;
    stopPlayback();
    const controller = new AbortController();
    exportControllerRef.current = controller;
    const current = () => visibleRef.current && exportControllerRef.current === controller && !controller.signal.aborted;
    const check = () => { if (!current()) throw controller.signal.reason ?? new Error("MP3 export cancelled."); };
    if (exportUrlRef.current) URL.revokeObjectURL(exportUrlRef.current);
    exportUrlRef.current = null; setExportDownload(null); setExportProgress(null);
    setIsExporting(true); setExportStatus("Saving screenplay before export…");
    let decoder: ScheduledAudio | undefined;
    const stopDecoder = () => decoder?.stop();
    controller.signal.addEventListener("abort", stopDecoder, { once: true });
    try {
      // Decode/resample all providers to one format; never enqueue audible playback.
      decoder = new ScheduledAudio(new AudioContext({ sampleRate: MP3_SAMPLE_RATE }));
      await decoder.ready;
      check();
      if (!await onSave()) throw new Error("Save failed. Save your screenplay successfully before exporting.");
      check();
      // Neutral baselines are cache artifacts, not additional screenplay speech.
      const sentences = playbackSentences.filter(s => !isLocalAudio(s) || s.silenceMs! > 0);
      const revisions = new Map<string, string>(), providers = new Map<string, string>();
      const memory = await cachedPlaybackUrls(sentences, revisions, providers);
      check();
      if (sentences.some(s => !isLocalAudio(s) && !revisions.has(s.voiceId))) throw new Error("An assigned voice is unavailable. Check your casting before exporting.");
      setExportStatus("Checking the entire screenplay’s audio…");
      const manifest = await playbackManifest(screenplayId, sentences, revisions);
      check();
      const { createMp3Writer } = await import("@/utils/mp3Encoder");
      check();
      const blob = await exportScreenplayMp3({
        clips: sentences.map((s, i) => ({ text: s.text, emotional: Boolean(s.synthesisOptions.ted_intonation || s.synthesisOptions.gemini_intonation),
          silenceMs: s.silenceMs,
          cached: memory.has(s.id) || manifest[i].cached, provider: providers.get(s.voiceId) })),
        signal: controller.signal, createWriter: createMp3Writer, estimates: playbackEstimatesRef.current,
        onProgress: progress => { if (current()) { setExportProgress(progress); setExportStatus(progress.message); } },
        prepare: async index => {
          check();
          const sentence = sentences[index];
          if (isLocalAudio(sentence)) return { audio: decoder!.silence(sentence.silenceMs!), persisted: true };
          const cachedUrl = memory.get(sentence.id) ?? manifest[index].url;
          if (cachedUrl) {
            let decoded:AudioBuffer|undefined;
            try { decoded=await decoder!.decode(cachedUrl);check(); }
            catch { check(); }
            if(decoded)return {audio:await processAudioEffects(decoded,sentence.audioVolume,sentence.audioSpeed,controller.signal),persisted:true};
          }
          // Missing/expired clips use the same exact casting, emotion, cache and
          // provider-budget protections as playback. Never substitute neutral.
          return prepareAuthoredAudio(sentence, async options => {
            check();
            const result = await loadPreview(sentence.voiceId, sentence.text, options, false, revisions.get(sentence.voiceId), sentence.cacheSources);
            check();
            if (result.voiceRevision !== revisions.get(sentence.voiceId)) {
              throw Object.assign(new Error("Voice settings changed. Please export again."), { retryable: false });
            }
            const audio = await processAudioEffects(await decoder!.decode(result.url),sentence.audioVolume,sentence.audioSpeed,controller.signal); check();
            return { audio, persisted: result.persisted };
          });
        },
      });
      check();
      // Catch admin actor edits during a long export, including cached clips.
      const finalRevisions = new Map<string, string>();
      await cachedPlaybackUrls(sentences, finalRevisions, new Map());
      check();
      if ([...revisions].some(([id, revision]) => finalRevisions.get(id) !== revision)) {
        throw new Error("Voice settings changed during export. Please export again.");
      }
      const url = URL.createObjectURL(blob), filename = mp3Filename(screenplayFilename);
      exportUrlRef.current = url; setExportDownload({ url, filename });
      const link = document.createElement("a");
      link.href = url; link.download = filename; document.body.appendChild(link); link.click(); link.remove();
      setExportStatus("MP3 ready. If your download did not start, select Download MP3.");
    } catch (error) {
      if (visibleRef.current && exportControllerRef.current === controller) {
        const message = controller.signal.aborted
          ? (controller.signal.reason instanceof Error ? controller.signal.reason.message : "MP3 export cancelled.")
          : error instanceof Error ? error.message : "Unable to export the screenplay.";
        setExportStatus(message); setExportProgress(null);
        if (!controller.signal.aborted) toast.error(message);
      }
    } finally {
      controller.signal.removeEventListener("abort", stopDecoder); stopDecoder();
      if (exportControllerRef.current === controller) { exportControllerRef.current = null; setIsExporting(false); }
    }
  };

  const playIndices = (indices: number[]) => {
    void playPreviewBlocks(
      indices
        .filter((index) => index >= 0 && index < blocks.length)
        .map((index) => ({ block: blocks[index] })),
      true,
    );
  };

  const sceneRange = sceneBlockRange(blocks, activeIndex);
  const selectedIndices = blocks
    .map((block, index) => ((checkedBlockIds.size?checkedBlockIds:selectedBlockIds).has(block.id) ? index : -1))
    .filter((index) => index >= 0);

  const rangeIndices = (range: [number, number] | null) =>
    range
      ? Array.from(
          { length: range[1] - range[0] + 1 },
          (_, offset) => range[0] + offset,
        )
      : [];

  const playbackScopeAvailable: Record<PlaybackScope, boolean> = {
    scene: Boolean(sceneRange),
    selected: selectedIndices.length > 0,
    highlight: Boolean(highlightedText),
    current: blocks.slice(activeIndex).some(block => Boolean(block.text.trim())),
    script: blocks.some((block) => Boolean(block.text.trim())),
  };

  const playCurrentScope = () => {
    if (isPlaying) {
      stopPlayback();
      return;
    }

    console.debug("[ScreenplayEditor] Playback requested:", {
      scope: playbackScope,
      activeBlockId,
      selectedCount: selectedIndices.length,
      hasHighlight: Boolean(highlightedText),
      sceneRange,
    });

    switch (playbackScope) {
      case "highlight": {
        if (!highlightedText) return;
        const block = blocks.find(
          (candidate) => candidate.id === highlightedText.blockId,
        );
        if (!block) return;
        void playPreviewBlocks([
          {
            block,
            text: highlightedText.text,
            selectionStart: highlightedText.start,
            selectionEnd: highlightedText.end,
          },
        ]);
        return;
      }
      case "selected":
        playIndices(selectedIndices);
        return;
      case "scene":
        playIndices(rangeIndices(sceneRange));
        return;
      case "current": {
        const offset = cursor?.blockId === activeBlockId && cursor.field === "text" ? cursor.start : 0;
        void playPreviewBlocks(blocks.slice(activeIndex).map((block, index) => index === 0
          ? { block, selectionStart: offset, selectionEnd: block.text.length } : { block }), true);
        return;
      }
      case "script":
        playIndices(blocks.map((_, index) => index));
        return;
    }
  };

  return (
    <Card ref={editorRootRef} className="flex w-full flex-col lg:h-[calc(100dvh-var(--editor-top,6rem))] lg:overflow-hidden lg:rounded-b-none"
      onPointerDownCapture={() => historyRef.current.breakGroup()}
      onBlurCapture={() => historyRef.current.breakGroup()}
      onCompositionStartCapture={() => historyRef.current.breakGroup()}
      onCompositionEndCapture={() => historyRef.current.breakGroup()}
      onKeyDownCapture={event => {
        const action = historyShortcut({ ...event, isComposing: event.nativeEvent.isComposing });
        const target = event.target as HTMLElement;
        if (action) {
          if (target.matches("input:not([data-editor-field]), textarea:not([data-editor-field]), [contenteditable=true]")) return;
          event.preventDefault(); event.stopPropagation(); restoreHistory(action);
        } else if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "Tab", "Enter", "Escape"].includes(event.key) || event.ctrlKey || event.metaKey) historyRef.current.breakGroup();
      }}>
      {editorToolbarNode&&createPortal(<div role="toolbar" aria-label="Editor toolbar" className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 border-b bg-card px-3 py-[2px] font-sans">
        <Button type="button" variant="outline" size="icon" className="h-8 w-8 shrink-0" aria-label={toolsCollapsed?'Show emotion and delivery panes':'Collapse emotion and delivery panes'}
          title={toolsCollapsed?'Restore emotion and delivery panes':'Collapse both control panes to the left'} aria-expanded={!toolsCollapsed}
          aria-controls={`${characterListId}-emotion-pane ${characterListId}-delivery-pane`} onClick={()=>setToolsCollapsed(value=>!value)}>
          {toolsCollapsed?<PanelLeftOpen className="h-4 w-4" aria-hidden="true"/>:<PanelLeftClose className="h-4 w-4" aria-hidden="true"/>}
        </Button>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <p className="max-w-48 truncate text-sm font-normal">{screenplayFilename?.replace(/\s*\(\d+\s+scenes?\)\s*$/i,'') || "Screenplay Editor"}</p>
            {summaryNode && createPortal(<>
            {!conversionAnalysis&&<span role="progressbar" aria-label="Screenplay audio complete" aria-valuemin={0} aria-valuemax={100}
              aria-valuenow={availableAudio.percent??undefined} aria-valuetext={preparation.status} title={preparation.status}
              className={`shrink-0 text-xs tabular-nums ${availableAudio.percent === 100 ? "text-primary" : "text-muted-foreground"}`}>
              {availableAudio.label}{availableAudio.percent !== null ? ' converted' : ''}
            </span>}
            {!conversionAnalysis&&showBlockConversion?<span role="status" aria-label="Block audio progress"
              title="A script block is converted when all of its current playback clips are verified. Neutral cache copies are excluded."
              className="sr-only">{preparation.progressStale?'Last confirmed: ':''}{blockConversion.checking?`Checking audio for ${blockConversion.total} block${blockConversion.total===1?'':'s'}…`:`Converted ${blockConversion.completed} of ${blockConversion.total} blocks`}{blockConversion.needsAttention>0?` · ${blockConversion.needsAttention} need help`:''}</span>
              :!conversionAnalysis&&preparation.progressKnown&&!preparation.cancelled&&sceneConversion.total>0&&<span role="status" aria-label="Scene audio progress"
              title="A scene is converted when every current playback clip, including its delivery and character-name announcements, is verified. Extra neutral cache copies are counted separately."
              className="sr-only">{preparation.progressStale?'Last confirmed: ':''}Converted {sceneConversion.completed} of {sceneConversion.total} scenes{sceneConversion.inProgress>0?` · ${sceneConversion.inProgress} in progress`:''}{sceneConversion.needsAttention>0?` · ${sceneConversion.needsAttention} need help`:''}</span>}
            </>, summaryNode)}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SentenceAudioRecoveryDialog screenplayId={screenplayId} visible={preparation.needsAttention} disabled={!!conversionAnalysis||isPlaying||isExporting||saveState.hasUnsavedChanges}/>
        </div>
        <fieldset disabled={!!conversionAnalysis} className="contents" aria-label="Editor actions">
          <ScreenplaySaveStatus state={saveState} onSave={onSave} showStatus={false} />
          <div ref={setExportToolbarNode} className="contents" />
          <div ref={setPerformanceActionsNode} className="shrink-0" aria-label="Pinned performance actions" />
          <Button type="button" variant="outline" size="icon" className="h-8 w-8" aria-label="Assign inflections"
            title="Assign inflections using scene context" disabled={!!conversionAnalysis||isPlaying||isExporting||isApplyingIntonation||assignInflectionsOpen}
            onClick={() => setAssignInflectionsOpen(true)}><Sparkles aria-hidden="true" className="h-4 w-4" /></Button>
          <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => restoreHistory("undo")}
            disabled={!!conversionAnalysis||!historyRef.current.canUndo} aria-label="Undo last change" aria-keyshortcuts="Control+Z Meta+Z"
            title="Undo last change (Ctrl+Z / ⌘Z). History lasts while this screenplay is open.">
            <Undo2 aria-hidden="true" className="h-4 w-4" />
          </Button>
          <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => restoreHistory("redo")}
            disabled={!!conversionAnalysis||!historyRef.current.canRedo} aria-label="Redo last change" aria-keyshortcuts="Control+Shift+Z Meta+Shift+Z Control+Y"
            title="Redo (Ctrl+Shift+Z / ⌘⇧Z / Ctrl+Y)"><Redo2 className="h-4 w-4" /></Button>
          <span role="status" aria-label="Editor undo status" className="sr-only">{historyStatus}</span>
        </fieldset>
        <ScriptZoomControl value={scriptZoom} onChange={value=>setScriptZoom(clampScriptZoom(value))}/>
        <div className="ml-auto min-w-0 py-1 text-right"><ScreenplaySaveStatus state={saveState} onSave={onSave} showButton={false}/></div>
      </div>,editorToolbarNode)}
      <CardContent className="min-h-0 flex-1 p-0">
        <EditorToolSectionGroup storageKey={EDITOR_TOOL_SECTIONS_KEY} dock={{target:rightToolsNode,active:activeRightTool,titles:rightTools.map(([title])=>title),groups:{Volume:'Voice controls',Speed:'Voice controls',Pitch:'Voice controls'}}}>
        <div ref={paneGridRef} data-tools-collapsed={toolsCollapsed} style={{'--script-right-pane-width':activeRightTool?'min(18rem,23vw)':'0px'} as CSSProperties} className="screenplay-editor-grid grid gap-0 lg:h-full lg:min-h-0 lg:grid-cols-[11rem_calc(12.8rem_-_16px)_0_minmax(0,1fr)_var(--script-right-pane-width,0px)_3rem] xl:grid-cols-[12rem_calc(13.6rem_-_16px)_0_minmax(0,1fr)_var(--script-right-pane-width,0px)_3rem] lg:grid-rows-[auto_minmax(0,1fr)_auto]">
          <div ref={setEditorToolbarNode} className="min-w-0 max-lg:order-3 lg:col-start-4 lg:row-start-1" />
        <fieldset disabled={!!conversionAnalysis} className="contents" aria-label="Screenplay content">
          <aside ref={emotionPaneRef} id={`${characterListId}-emotion-pane`} hidden={toolsCollapsed} className={`${toolsCollapsed?'hidden':'flex'} min-h-0 flex-col gap-3 p-3 font-sans max-lg:order-1 lg:col-start-1 lg:row-start-1 lg:row-span-3 lg:h-full lg:overflow-hidden`} aria-label="Screenplay editor controls">
        {assignInflectionsOpen && <AssignInflectionsDialog screenplayId={screenplayId} blocks={blocks} entryId={activeBlockId} onSave={onSave}
          onClose={() => setAssignInflectionsOpen(false)} onApply={(source, entries, replace) => {
            if (!visibleRef.current || JSON.stringify(blocksRef.current) !== JSON.stringify(source)) throw new Error('The screenplay changed during analysis. Start again with the current text.');
            const updated = applyDirectedEntries(source, entries, replace);
            // Validate through the existing audio planner before committing a single undoable edit.
            currentSentenceBlocks(updated.filter((b, i) => b !== source[i]).map(block => ({ block })), updated);
            publishBlocks(updated);
            setHighlightedText(null); setSelectionIssue('');
            toast.success('Inflections assigned. Existing conversion settings control audio preparation. Use Undo to restore the previous directions.');
          }} />}
        <section className="editor-themed-scroll min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain" aria-label="Emotion tools">
          <section className="space-y-3" aria-label="Emotion highlight controls">
            <GeminiDeliveryPanel
              selectionKey={deliverySelections.map(range => `${range.blockId}:${range.start}:${range.end}`).join('|') || activeBlockId}
              selected={!!deliverySelections.length && !selectedPause}
              canClearSelection={!selectionIssue&&(!!highlightedText&&!selectedPause||blocks.some(block=>selectedBlockIds.has(block.id)&&block.intonation?.spans.some(span=>!isPauseMarker(span)&&(!span.gemini?.cueMarker||!!span.gemini.volume||span.gemini.audioGainDb!==undefined||!!span.gemini.volumeMotion))))}
              current={selectedPause || !deliverySelections.length ? undefined : deliveryDirection}
              selectionIssue={selectionIssue}
              disabled={isApplyingIntonation || isPlaying || isExporting}
              voiceMessage={nonGeminiSelection
                ? "Choose a Gemini replacement on the Voices page to enable playback and delivery directions." : undefined}
              hasBlock={!!activeBlock?.text.trim()} rangeCount={activeTedSpans.filter(s => !isPauseMarker(s)).length}
              actionsContainer={performanceActionsNode}
              controlsContainer={performanceControlsNode}
              messagesContainer={performanceMessagesNode}
              cueOnly={markerOnly && !!selectedDelivery?.gemini?.cueMarker}
              onPatch={(direction, automatic) => void applyGeminiDelivery(direction, automatic)}
              onApply={(direction, automatic) => void applyGeminiDelivery(direction, automatic)} onClear={(wholeBlock, automatic) => void clearDelivery(wholeBlock, automatic)}
            />
          </section>
        </section>
          </aside>

          <aside ref={deliveryPaneRef} id={`${characterListId}-delivery-pane`} hidden={toolsCollapsed} className={`${toolsCollapsed?'hidden':''} editor-themed-scroll border-t p-3 font-sans max-lg:order-2 lg:col-start-2 lg:row-start-1 lg:row-span-3 lg:h-full lg:min-h-0 lg:overflow-y-auto lg:border-t-0`} aria-label="Script tools">
          <div ref={setPerformanceControlsNode} className="mb-5" aria-label="Performance controls" />
          <PointVocalCueTools disabled={!!conversionAnalysis||isPlaying||isExporting||isApplyingIntonation}
            onInsert={kind=>insertVocalCue(kind)} onDragStart={startDrag} onDragEnd={endDrag} />
          <div className="mt-5 space-y-5" aria-label="Language and pauses">
        <EditorToolSection title="Language" grouped scroll={false}>
        <section className="space-y-2" aria-label="Passage speech language">
          <Label htmlFor={`${characterListId}-passage-language`}>Selected block language</Label>
          <select id={`${characterListId}-passage-language`} className="h-9 w-full rounded-md border bg-background px-2 text-sm"
            value={activeBlock?.speechLanguage ?? 'inherit'} disabled={!activeBlock || isPlaying || isExporting || isApplyingIntonation}
            onChange={e => {
              if (!activeBlock) return;
              const value = e.target.value;
              if(value!==(activeBlock.speechLanguage??'inherit'))setPendingBlockLanguage({blockId:activeBlock.id,value});
            }}>
            <option value="inherit">Use screenplay language ({scriptLanguage === 'auto' ? 'automatic' : SPEECH_LANGUAGES[scriptLanguage]})</option>
            <option value="auto">Automatic detection for this block</option>
            {Object.entries(SPEECH_LANGUAGES).map(([code, name]) => <option key={code} value={code}>{name}</option>)}
          </select>
        </section>
        </EditorToolSection>
        {selectedPause && rightToolsNode && createPortal(<section hidden={activeRightTool!=='Vocal cues & pauses'} className="space-y-3 border-t pt-4" aria-labelledby={`${characterListId}-pause-tools`}>
        <h3 id={`${characterListId}-pause-tools`} className="text-xs font-semibold">{selectedPause ? 'Selected pause' : 'Pause length'}</h3>
        <section className="space-y-3" aria-label="Pause controls">
          {selectedPause && <>
            <Label htmlFor={`${characterListId}-pause-seconds`}>Pause length</Label>
            <select id={`${characterListId}-pause-seconds`} className="h-9 w-full rounded-md border bg-background px-2 text-sm"
              value={(selectedPause.gemini?.pauseMs ?? 1000) / 1000} disabled={isPlaying || isExporting || isApplyingIntonation}
              onChange={e => updateBlock(activeBlock.id, setInlinePauseDuration(activeBlock, selectedPause.id, Number(e.target.value)))}>
              {[1, 2, 3, 4, 5, 6, 7].map(seconds => <option key={seconds} value={seconds}>{seconds} second{seconds === 1 ? "" : "s"}</option>)}
            </select>
            <Button type="button" variant="ghost" size="sm" disabled={isPlaying || isExporting || isApplyingIntonation}
              onClick={() => {
                const result = replaceEditorText(activeBlock, selectedPause.start, selectedPause.end, "");
                updateBlock(activeBlock.id, result.block); setHighlightedText(null);
                setCursor({ blockId: activeBlock.id, field: "text", start: result.caret, end: result.caret, direction: "none" });
              }}>Remove pause</Button>
          </>}
        </section>
        </section>,rightToolsNode)}
          </div>
          </aside>

          <div className="script-document-layout editor-themed-scroll min-h-[70vh] max-lg:order-5 lg:col-start-4 lg:row-start-2 lg:h-full lg:min-h-0 lg:overflow-y-auto"
            onPointerLeave={()=>{setInspectedSpanId(null);setInspectedBlockId(null);}}>
        <div
          ref={screenplayDocumentRef}
          spellCheck={spellCheck}
          onFocusCapture={event => {
            const focus = editorFocus(event.target as Element);
            if (focus) { setWholeBlockSelection(false); setActiveBlockId(focus.blockId); setCursor(focus.field === "text" ? focus : null); }
          }}
          onDragOver={event => {
            const target = resolveDrop(event);
            if (!target) { setDropIndex(null); setPauseDrop(null); return; }
            event.preventDefault(); event.dataTransfer.dropEffect = "copy";
            setDropIndex("index" in target ? target.index : null); setPauseDrop("caret" in target ? target.caret : null);
          }}
          onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) { setDropIndex(null); setPauseDrop(null); } }}
          onDrop={event => {
            if (!dragKindRef.current || readEditorDrag(event.dataTransfer) !== dragKindRef.current) return;
            event.preventDefault();
            const target = resolveDrop(event); endDrag();
            if (!target) return;
            if ("caret" in target) insertVocalCue(target.kind, target.caret.blockId, target.caret.offset, true);
            else if (target.kind === "parenthetical") insertParenthetical(target.index - 1, true);
            else insertBlock(target.kind as ScreenplayEditorBlockType, target.index - 1);
          }}
          onBlurCapture={(event) => {
            if ((event.target as HTMLElement).matches("textarea, input")) preparation.flush?.();
          }}
          className="screenplay-document min-h-full w-full px-3 py-4 sm:pl-6 sm:pr-4"
          data-background={background}
          style={{ fontFamily: FONT_FAMILY,'--script-font-size':`${zoomMetrics.fontSize}px`,'--script-line-height':`${zoomMetrics.lineHeight}px` } as CSSProperties}
          role="region"
          aria-label="Screenplay document"
        >
          <datalist id={characterListId}>
            {recentCharacters.map((character) => <option key={character} value={character} />)}
          </datalist>

          <div className="space-y-2 pl-18">
            {blocks.map((block, index) => {
              const isActive = block.id === activeBlockId;
              const isSelected = selectedBlockIds.has(block.id);
              const isBlockPlaying = isPlaying && playingBlockId === block.id;
              const blockEmotionSpans = block.intonation?.spans ?? [];
              const blockSelection=highlightedText?.blockId===block.id&&highlightedText.text===block.text.slice(highlightedText.start,highlightedText.end)?highlightedText:undefined;
              const sceneNumber = sceneNumbersById.get(block.id);
              const hasBlockPlayback =
                block.type === "dialogue" ||
                block.type === "action" ||
                block.type === "transition";

              return (
                <Fragment key={block.id}>
                <ScriptBlockInsertBar position={index} disabled={isPlaying||isExporting||isApplyingIntonation||!!conversionAnalysis}
                  onInsert={kind=>kind==='parenthetical'?insertParenthetical(index-1,true):insertBlock(kind,index-1)} />
                <div data-audio-block={block.id} className="group/block relative script-block-row"
                  onPointerEnter={()=>{setInspectedBlockId(block.id);setInspectedSpanId(null);}}>
                  {dropIndex === index && <div role="status" aria-label="Insert block here" className="script-block-drop absolute -top-1 left-0 right-0 z-30 border-t-2 border-primary pointer-events-none"><span className="bg-primary px-2 text-xs font-sans text-primary-foreground">Insert here</span></div>}
                    {hasBlockPlayback && (
                      <Button
                        type="button"
                        variant={isBlockPlaying ? "destructive" : "outline"}
                        size="icon"
                        className="absolute left-0 top-1 z-20 h-8! w-8! script-control script-play-control p-0"
                        data-playing={isBlockPlaying}
                        disabled={!isBlockPlaying && (isPlaying || isExporting || Boolean(previewDisabledReason) || !block.text.trim())}
                        onPointerDown={event=>{
                          // Keep the text caret/selection and layout stable until Click.
                          // Keyboard activation still uses the native button semantics.
                          if(event.button===0)event.preventDefault();
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (isBlockPlaying) stopPlayback();
                          else void playPreviewBlocks([{ block }], true);
                        }}
                        aria-label={`${isBlockPlaying ? "Stop" : "Play"} ${blockLabel(block.type).toLowerCase()} block ${index + 1}`}
                        title={`${isBlockPlaying ? "Stop" : "Play"} ${blockLabel(block.type).toLowerCase()}`}
                      >
                        {isBlockPlaying ? <Square className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                      </Button>
                    )}

                  <label className={`absolute left-0 ${hasBlockPlayback?'top-10':'top-7'} z-20 flex h-8 w-8 cursor-pointer items-center justify-center script-control script-select-control`}
                    title="Select block" onClick={event=>event.stopPropagation()}>
                    <input type="checkbox" className="h-4 w-4 accent-primary" aria-label={`Select ${blockLabel(block.type).toLowerCase()} block ${index+1}`}
                      checked={checkedBlockIds.has(block.id)} disabled={isPlaying||isExporting} onChange={event=>checkBlock(block.id,index,event.currentTarget.checked)} />
                  </label>

                  {sceneNumber && <button
                    type="button"
                    className="absolute left-0 top-1 h-5 min-w-5 rounded border px-1 text-[9px] font-bold script-control"
                    onClick={(event) => {
                      event.stopPropagation();
                      selectBlock(block.id, index, event.shiftKey, true); setCursor(null);
                    }}
                    aria-label={`Scene ${sceneNumber}`}
                    title="Select scene"
                  >
                    {sceneNumber}
                  </button>}

                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="absolute right-1 top-1 z-20 h-[26px]! w-[26px]! script-control script-delete-control p-0 transition-opacity"
                    onClick={(event) => {
                      event.stopPropagation();
                      removeBlock(block.id);
                    }}
                    aria-label={`Delete ${blockLabel(block.type).toLowerCase()} block`}
                    title="Delete block"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>

                <article
                  className={`relative rounded border py-1.5 pl-10 pr-10 transition ${isSelected ? "script-selected" : isActive ? "border-slate-400" : "border-transparent hover:border-slate-300"}`}
                  onClick={event=>selectBlock(block.id,index,event.shiftKey,!(event.target as HTMLElement).closest('[data-editor-field]'))}
                  aria-label={`${blockLabel(block.type)} block ${index + 1}`}
                >
                  {block.type === "dialogue" && (
                    <div>
                      <div className="mx-auto max-w-[38ch]">
                        <Input
                          data-editor-block={block.id}
                          data-editor-field="character"
                          list={characterListId}
                          value={block.character ?? ""}
                          onChange={(event) => updateBlock(block.id, { character: event.target.value.toUpperCase() }, textEdit(event))}
                          onFocus={(event) => { setActiveBlockId(block.id); event.currentTarget.select(); }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              focusBlock(block.id, "text");
                            }
                          }}
                          placeholder="CHARACTER"
                          className={`mx-auto h-7 max-w-[24ch] border-transparent bg-transparent py-0 text-center font-mono font-bold uppercase text-inherit focus-visible:border-slate-400 focus-visible:ring-0 ${screenplayFontSizeClass}`}
                          style={{ fontFamily: FONT_FAMILY }}
                          aria-label={`Character name for block ${index + 1}`}
                        />
                        {block.parenthetical !== undefined && (
                          <Input
                            data-editor-block={block.id}
                            data-editor-field="parenthetical"
                            value={block.parenthetical}
                            onChange={(event) => updateBlock(block.id, {
                              parenthetical: event.target.value,
                              parentheticalId: block.parentheticalId ?? crypto.randomUUID(),
                            }, textEdit(event))}
                            onFocus={(event) => {
                              setActiveBlockId(block.id);
                              if (event.currentTarget.value === "()") {
                                event.currentTarget.setSelectionRange(1, 1);
                              }
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                focusBlock(block.id, "text");
                              }
                            }}
                            placeholder="(casually)"
                            className={`mx-auto h-7 max-w-[30ch] border-transparent bg-transparent py-0 text-center font-mono text-inherit focus-visible:border-slate-400 focus-visible:ring-0 ${screenplayFontSizeClass}`}
                            style={{ fontFamily: FONT_FAMILY }}
                            aria-label={`Parenthetical direction for ${block.character || "character"}`}
                          />
                        )}
                        <PerformanceSegmentLabels text={block.text} spans={blockEmotionSpans} selection={blockSelection} onInspect={span=>{setInspectedBlockId(block.id);setInspectedSpanId(span?.id??null);}}>
                          {blockEmotionSpans.length > 0 && (
                            <div
                              aria-hidden="true" data-performance-text-layer
                              className={`pointer-events-none absolute inset-0 z-0 overflow-hidden whitespace-pre-wrap wrap-break-word rounded-md border border-transparent px-1 py-0.5 text-left font-mono text-inherit ${screenplayFontSizeClass}`}
                              style={{ fontFamily: FONT_FAMILY }}
                            >
                              {performanceTextLayer(block.text, blockEmotionSpans)}
                            </div>
                          )}
                          <Textarea
                            data-editor-block={block.id}
                            data-editor-field="text"
                            value={block.text}
                            rows={rowsForText(block.text, 1)}
                            wrap="soft"
                            onChange={(event) => {
                              fitTextArea(event.currentTarget);
                              updateBlockText(block, event.target.value, textEdit(event));
                            }}
                            onSelect={(event) => handleTextSelection(block.id, event.currentTarget)}
                            onPointerDown={() => { rejectedSelectionRef.current = null; }}
                            onClick={(event) => handleTextSelection(block.id, event.currentTarget)}
                            onKeyDown={(event) => handleEditorShortcut(event, block, index)}
                            placeholder="Dialogue"
                            className={`min-h-0 resize-none overflow-hidden whitespace-pre-wrap wrap-break-word border-transparent bg-transparent px-1 py-0.5 text-left font-mono focus-visible:border-slate-400 focus-visible:ring-0 ${screenplayFontSizeClass} ${blockEmotionSpans.length > 0 ? "relative z-10 text-transparent script-highlight-input" : "text-inherit"}`}
                            style={{ fontFamily: FONT_FAMILY }}
                            aria-label={`Dialogue for ${block.character || "character"}`}
                          />
                        </PerformanceSegmentLabels>
                      </div>
                    </div>
                  )}

                  {block.type !== "dialogue" && (
                    <PerformanceSegmentLabels text={block.text} spans={blockEmotionSpans} selection={blockSelection} onInspect={span=>{setInspectedBlockId(block.id);setInspectedSpanId(span?.id??null);}}>
                      {blockEmotionSpans.length > 0 && (
                        <div
                          aria-hidden="true" data-performance-text-layer
                          className={`pointer-events-none absolute inset-0 z-0 overflow-hidden whitespace-pre-wrap wrap-break-word rounded-md border border-transparent px-1 py-0.5 font-mono text-inherit ${screenplayFontSizeClass} ${block.type === "scene-heading" ? "font-bold uppercase" : ""} ${block.type === "transition" ? "text-right font-bold uppercase" : "text-left"}`}
                          style={{ fontFamily: FONT_FAMILY }}
                        >
                          {performanceTextLayer(block.text, blockEmotionSpans)}
                        </div>
                      )}
                      <Textarea
                        data-editor-block={block.id}
                        data-editor-field="text"
                        value={block.text}
                        rows={rowsForText(block.text, 1)}
                        wrap="soft"
                        onChange={(event) => {
                          fitTextArea(event.currentTarget);
                          updateBlockText(block, block.type === "scene-heading" || block.type === "transition"
                              ? event.target.value.toUpperCase().replaceAll("[PAUSE]", "[Pause]")
                              : event.target.value, textEdit(event));
                        }}
                        onSelect={(event) => handleTextSelection(block.id, event.currentTarget)}
                        onPointerDown={() => { rejectedSelectionRef.current = null; }}
                        onClick={(event) => handleTextSelection(block.id, event.currentTarget)}
                        onKeyDown={(event) => handleEditorShortcut(event, block, index)}
                        placeholder={block.type === "scene-heading" ? "INT. LOCATION - DAY" : block.type === "transition" ? "CUT TO:" : "Narration or action"}
                        className={`min-h-0 resize-none overflow-hidden whitespace-pre-wrap wrap-break-word border-transparent bg-transparent px-1 py-0.5 font-mono focus-visible:border-slate-400 focus-visible:ring-0 ${screenplayFontSizeClass} ${blockEmotionSpans.length > 0 ? "relative z-10 text-transparent script-highlight-input" : "text-inherit"} ${block.type === "scene-heading" ? "font-bold uppercase" : ""} ${block.type === "transition" ? "text-right font-bold uppercase" : "text-left"}`}
                        style={{ fontFamily: FONT_FAMILY }}
                        aria-label={`${blockLabel(block.type)} text`}
                      />
                    </PerformanceSegmentLabels>
                  )}

                </article>
                </div>
                </Fragment>
              );
            })}
            <ScriptBlockInsertBar position={blocks.length} disabled={isPlaying||isExporting||isApplyingIntonation||!!conversionAnalysis}
              onInsert={kind=>kind==='parenthetical'?insertParenthetical(blocks.length-1,true):insertBlock(kind,blocks.length-1)} />
            {dropIndex === blocks.length && <div role="status" aria-label="Insert block here" className="relative border-t-2 border-primary pointer-events-none"><span className="absolute bg-primary px-2 text-xs font-sans text-primary-foreground">Insert here</span></div>}
          </div>
          {pauseDrop && <div aria-label="Pause insertion point" className="fixed z-50 w-0.5 bg-primary pointer-events-none"
            style={{ left: pauseDrop.x, top: pauseDrop.y, height: pauseDrop.height }} />}
        </div>
          </div>

          <aside hidden={!activeRightTool} aria-label="Additional script tools" className="editor-themed-scroll min-h-0 min-w-0 border-l bg-card p-3 font-sans max-lg:order-4 lg:col-start-5 lg:row-start-1 lg:row-span-3 lg:overflow-y-auto">
            <div className="mb-4 flex items-center justify-between gap-2"><h2 className={activeRightTool==='Voice controls'?'sr-only':'text-sm font-semibold'}>{activeRightTool}</h2>
              <Button type="button" variant="ghost" size="icon" className="ml-auto h-8 w-8 shrink-0" aria-label="Close script tools" onClick={()=>setActiveRightTool(null)}><X aria-hidden="true" className="h-4 w-4"/></Button>
            </div><div ref={setRightToolsNode} id={`${characterListId}-right-tools`} className="performance-palette space-y-4"/>
          </aside>
          <nav aria-label="Additional script tool shortcuts" className="flex gap-1 border-l bg-card p-1 font-sans max-lg:order-3 max-lg:flex-wrap lg:col-start-6 lg:row-start-2 lg:row-span-2 lg:flex-col lg:items-center">
            {rightTools.map(([title,Icon])=><Button key={title} type="button" variant={activeRightTool===title?'secondary':'ghost'} size="icon"
              aria-label={`Open ${title.toLowerCase()} tools`} title={title==='Voice controls'?'Volume, Speed, and Pitch':title} aria-pressed={activeRightTool===title} aria-expanded={activeRightTool===title}
              aria-controls={`${characterListId}-right-tools`} onClick={()=>setActiveRightTool(current=>current===title?null:title)}><Icon aria-hidden="true" className="h-4 w-4"/></Button>)}
          </nav>

          {playbackToolbarNode && createPortal(<><fieldset disabled={!!conversionAnalysis} className="contents"><div
            className="contents"
            aria-label="Screenplay playback controls"
          >
            <div className="contents">
              <Button
                type="button"
                variant={isPlaying ? "destructive" : "default"}
                size="icon"
                className="h-9 w-9 shrink-0"
                disabled={
                  !isPlaying &&
                  (isExporting || Boolean(previewDisabledReason) ||
                    !previewVoiceId ||
                    !playbackScopeAvailable[playbackScope])
                }
                onClick={playCurrentScope}
                aria-label={isPlaying ? "Stop playback" : `Play ${playbackScope}`}
                title={isPlaying ? "Stop playback" : `Play ${playbackScope}`}
              >
                {isPlaying ? (
                  <Square className="h-4 w-4" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
              </Button>

              <RadioGroup
                value={playbackScope}
                onValueChange={(value) => setPlaybackScope(value as PlaybackScope)}
                className="flex flex-wrap items-center gap-x-1 gap-y-1"
                aria-label="Playback range"
              >
                {(
                  [
                    ["scene", "Scene"],
                    ["selected", "Selected"],
                    ["highlight", "Highlighted"],
                    ["current", "From current"],
                  ] as const
                ).map(([value, label]) => (
                  <Label key={value} htmlFor={`${characterListId}-playback-scope-${value}`}
                    className={`flex min-h-9 select-none items-center gap-1.5 rounded px-2 py-1.5 text-xs ${playbackScopeAvailable[value]?'cursor-pointer':'cursor-not-allowed opacity-50'}`}
                    onPointerDown={event=>{
                      if(event.button===0&&playbackScopeAvailable[value])event.currentTarget.querySelector<HTMLButtonElement>('[role="radio"]')?.focus({preventScroll:true});
                    }}>
                    <RadioGroupItem
                      id={`${characterListId}-playback-scope-${value}`}
                      value={value}
                      disabled={!playbackScopeAvailable[value]}
                    />
                    <span>{label}</span>
                  </Label>
                ))}
              </RadioGroup>
              <div ref={setSummaryNode} aria-label="Screenplay audio status" className="ml-auto flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1" />

              {exportToolbarNode&&createPortal(<>{isExporting ? (
                <Button type="button" variant="outline" size="sm" aria-label="Cancel MP3 export"
                  onClick={() => exportControllerRef.current?.abort(new Error("MP3 export cancelled."))}>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />Cancel export
                </Button>
              ) : (
                <Button type="button" variant="outline" size="icon" className="h-8 w-8" aria-label="Save screenplay as MP3"
                  title="Download the entire screenplay with its assigned voices and emotional delivery. Missing audio will be prepared using your existing generation limits."
                  disabled={isPlaying || isApplyingIntonation || Boolean(previewDisabledReason) || !playbackSentences.length}
                  onClick={() => void saveScreenplayMp3()}>
                  <Download aria-hidden="true" className="h-4 w-4" />
                </Button>
              )}
              {exportDownload && <a className="text-xs text-primary underline" href={exportDownload.url}
                download={exportDownload.filename}>Download MP3</a>}
              </>,exportToolbarNode)}
            </div>

            {isExporting && exportProgress && <progress className="mt-2 h-1.5 w-full accent-primary"
              max={100} value={exportProgress.percent} aria-label="Screenplay MP3 export progress" />}
            {playbackProgress && (
              <progress className="mt-2 h-1.5 w-full accent-primary" max={100} value={playbackProgress.percent}
                aria-label="Selected playback audio loaded" />
            )}
          </div></fieldset>
          <div className="script-player-details basis-full min-w-0 space-y-1" aria-label="Playback messages">
            <PerformanceAttributeGutter span={inspectedSpan} language={inspectedBlock?.speechLanguage}/>
            <div ref={setPerformanceMessagesNode}/>
            <p className="text-xs text-muted-foreground empty:hidden" aria-live="polite">
              {playbackStatus || previewDisabledReason}
            </p>
            {exportStatus && <p className="text-xs text-muted-foreground" role="status" aria-label="MP3 export status">{isExporting ? `${exportProgress?.percent ?? 0}%` : exportStatus}</p>}
            {intonationPreparation && <p className="text-xs text-muted-foreground" role="status" aria-label="Emotion audio preparation">{intonationPreparation}</p>}
            {!conversionAnalysis&&preparation.notice && <p role="status" className="text-xs text-muted-foreground">
              {preparation.needsAttention && isAdministrator ? "Some audio needs administrator review. Check Error logs before authorizing a replacement; earlier charges may be uncertain." : preparation.notice}
            </p>}
            {conversionAnalysis&&<div className="max-w-2xl space-y-1 text-xs" aria-label="Conversion analysis">
              <p role="status" aria-live="polite">Inflection analysis {conversionAnalysis.working?'in progress':'paused'} · Analyzed {conversionAnalysis.completed} of {conversionAnalysis.total} pages</p>
              <progress className="h-2 w-full accent-primary" aria-label="AI inflection analysis progress" max={conversionAnalysis.total} value={conversionAnalysis.completed}/>
              {conversionAnalysis.error?<p role="alert" className="text-destructive">{conversionAnalysis.error}</p>:<p className="text-muted-foreground">{conversionAnalysis.status}</p>}
              <p className="text-muted-foreground">Keep this tab open. Editing and new audio are held until analysis finishes; completed batches are saved for reuse.</p>
              {conversionAnalysis.working?conversionAnalysis.onPause&&<Button size="sm" variant="outline" onClick={conversionAnalysis.onPause}>Pause analysis</Button>:<div className="flex gap-2">
                <Button size="sm" onClick={conversionAnalysis.onResume}>Continue analysis and prepare audio</Button>
                {conversionAnalysis.onCancel&&<Button size="sm" variant="outline" onClick={conversionAnalysis.onCancel}>Cancel analysis setup</Button>}
              </div>}
            </div>}
          </div></>, playbackToolbarNode)}
        </fieldset>
          <div className="min-w-0 max-lg:order-6 lg:col-start-4 lg:row-start-3">
            <div ref={setPlaybackToolbarNode} role="toolbar" aria-label="Script media player"
              className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2 border-t bg-card px-3 py-2 font-sans" />
          </div>
        </div>
        </EditorToolSectionGroup>
      </CardContent>
      <Dialog open={pendingBlockLanguage!==null} onOpenChange={open=>{if(!open)setPendingBlockLanguage(null);}}>
        <DialogContent><DialogHeader><DialogTitle>Change block language?</DialogTitle>
          <DialogDescription>This changes the language instruction for this whole block and may require new audio, consuming conversion credit under the existing limits. No change is applied until you confirm.</DialogDescription></DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={()=>setPendingBlockLanguage(null)}>Cancel block language change</Button>
            <Button disabled={isPlaying||isExporting||isApplyingIntonation} onClick={()=>{
              if(!pendingBlockLanguage)return;
              const block=blocksRef.current.find(b=>b.id===pendingBlockLanguage.blockId);
              if(block)updateBlock(block.id,{...block,speechLanguage:pendingBlockLanguage.value==='inherit'?undefined:restoreSpeechLanguage(pendingBlockLanguage.value)});
              setPendingBlockLanguage(null);
            }}>Apply block language</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
